import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Download, Phone, RefreshCw, Package, Truck, Printer,
  Save, Trash2, Search, Eye, Pencil, MapPin, Box,
  Loader2, CheckCircle2, X, ChevronUp, ChevronDown,
} from 'lucide-react';
import { adminAPI, websiteSettingsAPI } from '../../services/api';
import { useIsMobile } from '../../hooks/useIsMobile';
import '../../styles/AdminGlobal.css';
import './Admin.css';
import './OrderManagement.css';
import '../../components/admin/MobileCardList.css';

const STATUS_OPTIONS = ['preorder', 'pending', 'no_response', 'processing', 'shipped', 'delivered', 'cancelled'] as const;
type StatusFilter = 'all' | 'new' | 'future' | 'not_response' | 'confirmed' | 'confirmed_sent' | 'cancelled'
                  | 'preorder' | 'pending' | 'no_response' | 'processing' | 'shipped' | 'delivered';

// Which individual statuses belong to each group
const STATUS_GROUPS: Record<string, string[]> = {
  new:          ['pending'],
  future:       ['preorder'],
  not_response: ['no_response'],
  confirmed:    ['processing', 'shipped', 'delivered'],
  cancelled:    ['cancelled'],
};

const OrderManagement: React.FC = () => {
  const isMobile = useIsMobile();
  const [orders, setOrders] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('new');
  const [sendingToSteadfast, setSendingToSteadfast] = useState<number | null>(null);

  // Bulk Send to Steadfast (create consignments)
  const [bulkSending, setBulkSending] = useState(false);
  const [bulkSendResult, setBulkSendResult] = useState<{ sent: number; failed: number; already_sent: number; results: any[] } | null>(null);

  // Pickup Request (physical pickup / manifest)
  const [pickupRequesting, setPickupRequesting] = useState(false);
  const [pickupResult, setPickupResult] = useState<{ requested: number; already_requested: number; total_cod: number; pickup_time: string; results: any[]; pickup_api_success?: boolean; pickup_api_error?: string; pickup_id?: number } | null>(null);

  const [selectedRows, setSelectedRows] = useState<any[]>([]);

  // Bulk Category Assignment
  const [assignCategory, setAssignCategory] = useState<string>('');
  const [bulkAssigning, setBulkAssigning] = useState(false);

  // Per-row inline category update
  const [categoryUpdating, setCategoryUpdating] = useState<Set<number>>(new Set());

  // Print Labels
  const [printingLabel, setPrintingLabel] = useState<number | null>(null);
  const [bulkPrinting, setBulkPrinting] = useState(false);

  // Steadfast Status Sync
  const [syncing, setSyncing] = useState(false);
  const [lastSyncTime, setLastSyncTime] = useState<string>('');
  const [syncStats, setSyncStats] = useState<{ synced: number; changed: number } | null>(null);

  const [viewModalOpen, setViewModalOpen] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState<any>(null);

  // Edit modal state
  const [deliveryOptions, setDeliveryOptions] = useState<any[]>([]);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editModalTab, setEditModalTab] = useState<'edit' | 'slip' | 'fraud'>('edit');
  const [editingOrder, setEditingOrder] = useState<any>(null);
  const [editForm, setEditForm] = useState({
    customer_name: '',
    customer_phone: '',
    shipping_address: '',
    notes: '',
    status: 'pending',
    shipping_cost: '60',
    delivery_option: null as number | null,
  });
  const [editSaving, setEditSaving] = useState(false);
  const [editError, setEditError] = useState<string>('');

  // Admin discount state
  const [discountType, setDiscountType] = useState<'flat' | 'percentage' | ''>('');
  const [discountValue, setDiscountValue] = useState<string>('');
  const [discountSaving, setDiscountSaving] = useState(false);
  const [discountError, setDiscountError] = useState<string>('');

  // Customer Steadfast History
  const [customerHistory, setCustomerHistory] = useState<any>(null);
  const [customerHistoryLoading, setCustomerHistoryLoading] = useState(false);
  const [customerHistoryError, setCustomerHistoryError] = useState<string>('');

  const [siteSettings, setSiteSettings] = useState<{ site_name: string; support_phone: string; logo_url?: string }>({ site_name: '', support_phone: '' });
  const packingSlipRef = useRef<HTMLDivElement>(null);

  // Date range filter
  const [startDate, setStartDate] = useState<string>('');
  const [endDate, setEndDate] = useState<string>('');

  // Table: sort & pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [sortField, setSortField] = useState<string>('created_at');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');

  // Manual Order creation
  const [manualModalOpen, setManualModalOpen] = useState(false);
  const [manualSaving, setManualSaving] = useState(false);
  const [manualError, setManualError] = useState<string>('');
  const [manualSuccess, setManualSuccess] = useState<string>('');
  const [manualProductSearch, setManualProductSearch] = useState<string>('');
  const [manualProductResults, setManualProductResults] = useState<any[]>([]);
  const [manualProductSearching, setManualProductSearching] = useState(false);
  const [manualCartItems, setManualCartItems] = useState<any[]>([]);
  const [manualPickingProduct, setManualPickingProduct] = useState<any>(null);
  const [manualPickAttrs, setManualPickAttrs] = useState<{ color: string; size: string; weight: string; qty: number }>({ color: '', size: '', weight: '', qty: 1 });
  const [manualForm, setManualForm] = useState({
    customer_name: '',
    customer_phone: '',
    shipping_address: '',
    notes: '',
    payment_method: 'cash_on_delivery',
    shipping_cost: '60',
    initial_status: 'pending',
    coupon_code: '',
    send_email: false,
  });
  const [manualCouponApplied, setManualCouponApplied] = useState<{ code: string; discount: number } | null>(null);
  const [manualCouponChecking, setManualCouponChecking] = useState(false);
  const [manualCouponError, setManualCouponError] = useState<string>('');
  const manualSearchRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const manualItemsSubtotal = manualCartItems.reduce((sum, it) => sum + it.price * it.qty, 0);
  const manualShipping = parseFloat(manualForm.shipping_cost) || 0;
  const manualCouponDiscount = manualCouponApplied?.discount || 0;
  const manualTotal = manualItemsSubtotal + manualShipping - manualCouponDiscount;

  const handleManualProductSearch = (query: string) => {
    setManualProductSearch(query);
    if (manualSearchRef.current) clearTimeout(manualSearchRef.current);
    if (!query.trim()) { setManualProductResults([]); return; }
    manualSearchRef.current = setTimeout(async () => {
      setManualProductSearching(true);
      try {
        const { productAPI } = await import('../../services/api');
        const res = await productAPI.getProducts({ search: query, page: 1, limit: 10 } as any);
        const results = (res as any).results || res;
        setManualProductResults(Array.isArray(results) ? results : []);
      } catch { setManualProductResults([]); }
      finally { setManualProductSearching(false); }
    }, 400);
  };

  const handleManualAddToCart = () => {
    if (!manualPickingProduct) return;
    const existing = manualCartItems.findIndex(
      it => it.product_id === manualPickingProduct.id &&
            it.color === manualPickAttrs.color &&
            it.size === manualPickAttrs.size &&
            it.weight === manualPickAttrs.weight
    );
    if (existing >= 0) {
      const updated = [...manualCartItems];
      updated[existing].qty += manualPickAttrs.qty;
      setManualCartItems(updated);
    } else {
      setManualCartItems(prev => [...prev, {
        product_id: manualPickingProduct.id,
        name: manualPickingProduct.name_en,
        image: manualPickingProduct.image || manualPickingProduct.images?.[0]?.image || '',
        price: parseFloat(manualPickingProduct.discount_price || manualPickingProduct.price),
        stock: manualPickingProduct.stock,
        qty: manualPickAttrs.qty,
        color: manualPickAttrs.color,
        size: manualPickAttrs.size,
        weight: manualPickAttrs.weight,
      }]);
    }
    setManualPickingProduct(null);
    setManualPickAttrs({ color: '', size: '', weight: '', qty: 1 });
    setManualProductSearch('');
    setManualProductResults([]);
  };

  const handleManualApplyCoupon = async () => {
    const code = manualForm.coupon_code.trim();
    if (!code) return;
    setManualCouponChecking(true);
    setManualCouponError('');
    setManualCouponApplied(null);
    try {
      const res = await adminAPI.createManualOrder({
        customer_name: 'test', customer_phone: '0',
        shipping_address: 'test', items: [{ product_id: manualCartItems[0]?.product_id || 0, quantity: 1 }],
        coupon_code: code, _validate_only: true,
      });
      // If it gets here it's valid — but we just use the validate endpoint
      // Instead, call the validate coupon endpoint directly
      setManualCouponError('');
    } catch (err: any) {
      const msg = err?.response?.data?.coupon_code?.[0] || err?.response?.data?.non_field_errors?.[0] || '';
      if (msg) { setManualCouponError(msg); return; }
    } finally { setManualCouponChecking(false); }
    // Estimate discount locally: fetch coupon via adminAPI coupons list
    try {
      const coupons: any = await adminAPI.getCoupons();
      const list = coupons.data?.results || coupons.data || [];
      const found = list.find((c: any) => c.code === code && c.is_active);
      if (found) {
        let disc = 0;
        if (found.discount_type === 'percentage') {
          disc = (manualItemsSubtotal * found.discount_value) / 100;
          if (found.max_discount) disc = Math.min(disc, found.max_discount);
        } else {
          disc = parseFloat(found.discount_value);
        }
        setManualCouponApplied({ code, discount: disc });
      } else {
        setManualCouponError('Invalid or inactive coupon');
      }
    } catch { setManualCouponError('Could not validate coupon'); }
  };

  const handleManualCreateOrder = async () => {
    setManualError('');
    if (!manualForm.customer_name.trim()) { setManualError('Customer name is required'); return; }
    if (!manualForm.customer_phone.trim()) { setManualError('Phone number is required'); return; }
    if (!manualForm.shipping_address.trim()) { setManualError('Shipping address is required'); return; }
    if (manualCartItems.length === 0) { setManualError('Add at least one product'); return; }
    setManualSaving(true);
    try {
      const payload: any = {
        customer_name: manualForm.customer_name.trim(),
        customer_phone: manualForm.customer_phone.trim(),
        shipping_address: manualForm.shipping_address.trim(),
        notes: manualForm.notes.trim(),
        payment_method: manualForm.payment_method,
        shipping_cost: parseFloat(manualForm.shipping_cost) || 60,
        initial_status: manualForm.initial_status,
        send_email: manualForm.send_email,
        items: manualCartItems.map(it => ({
          product_id: it.product_id,
          quantity: it.qty,
          selected_color: it.color || '',
          selected_size: it.size || '',
          selected_weight: it.weight || '',
        })),
      };
      if (manualCouponApplied) payload.coupon_code = manualCouponApplied.code;
      const res: any = await adminAPI.createManualOrder(payload);
      const newOrder = res.data;
      setOrders(prev => [newOrder, ...prev]);
      setManualSuccess(`Order ${newOrder.order_number} created successfully!`);
      setTimeout(() => {
        setManualModalOpen(false);
        setManualSuccess('');
        setManualCartItems([]);
        setManualForm({ customer_name: '', customer_phone: '', shipping_address: '', notes: '', payment_method: 'cash_on_delivery', shipping_cost: '60', initial_status: 'pending', coupon_code: '', send_email: false });
        setManualCouponApplied(null);
        setManualCouponError('');
      }, 1500);
    } catch (err: any) {
      const data = err?.response?.data;
      if (typeof data === 'object') {
        const msgs = Object.entries(data).map(([k, v]) => `${k}: ${Array.isArray(v) ? v.join(', ') : v}`).join(' | ');
        setManualError(msgs);
      } else {
        setManualError('Failed to create order. Please check inputs.');
      }
    } finally { setManualSaving(false); }
  };

  // ── columnDefs removed: using custom HTML table ──
  // placeholder to mark removal start
  // ── column definitions replaced with custom HTML table ──

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    loadOrders();
    loadDeliveryOptions();
  }, []);

  const loadDeliveryOptions = async () => {
    try {
      const res = await adminAPI.getDeliveryOptions();
      const data = res.data || res;
      const list = Array.isArray(data) ? data : (data.results || []);
      setDeliveryOptions(list);
    } catch (err) {
      console.error('Failed to load delivery options:', err);
    }
  };

  const handleDeliveryOptionChange = (optionId: number | null) => {
    if (optionId === null) {
      setEditForm(prev => ({ ...prev, delivery_option: null }));
      return;
    }
    const found = deliveryOptions.find(opt => opt.id === optionId);
    if (found) {
      setEditForm(prev => ({
        ...prev,
        delivery_option: found.id,
        shipping_cost: String(found.cost),
      }));
    } else {
      setEditForm(prev => ({ ...prev, delivery_option: optionId }));
    }
  };

  // Listen for refresh event from AdminLayout header
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    const handleRefresh = () => {
      loadOrders();
    };
    window.addEventListener('admin-refresh', handleRefresh);
    return () => window.removeEventListener('admin-refresh', handleRefresh);
  }, []);

  // Auto-reload customer history when editingOrder changes (e.g., after Steadfast operations)
  useEffect(() => {
    if (editingOrder && editModalOpen) {
      // Find the latest order data from the orders list
      const updatedOrder = orders.find(o => o.id === editingOrder.id);
      if (updatedOrder) {
        // Update editingOrder with fresh data
        setEditingOrder(updatedOrder);
        // Reload customer history to reflect any new Steadfast deliveries
        if (updatedOrder.customer_phone) {
          loadCustomerHistory(updatedOrder.customer_phone);
        }
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orders, editModalOpen]);

  const loadOrders = async () => {
    try {
      setLoading(true);
      const response = await adminAPI.getOrders();
      const data = response.data || response;
      const ordersList = Array.isArray(data) ? data : (data.results || []);
      console.log('Orders loaded:', ordersList.length, 'First order:', ordersList[0]);
      setOrders(ordersList);
    } catch (error) {
      console.error('Failed to load orders:', error);
      setOrders([]);
    } finally {
      setLoading(false);
    }
  };

  const handleSendToSteadfast = async (orderId: number) => {
    if (!window.confirm('Are you sure you want to send this order to Steadfast Courier?')) {
      return;
    }

    setSendingToSteadfast(orderId);
    try {
      const response = await adminAPI.sendOrderToSteadfast(orderId);
      const data = response.data;
      
      if (data.success) {
        alert(`✅ Order sent to Steadfast successfully!\n\nConsignment ID: ${data.consignment_id}\nTracking Code: ${data.tracking_code}`);
        // Reload orders to get updated data
        await loadOrders();
      } else {
        alert(`Failed to send order to Steadfast: ${data.error || 'Unknown error'}`);
      }
    } catch (error: any) {
      console.error('Failed to send order to Steadfast:', error?.response?.status, error?.response?.data || error?.message || error);
      
      // Extract detailed error from response
      const responseData = error.response?.data;
      let errorMessage = 'Failed to send order to Steadfast.';
      
      if (responseData) {
        // Handle case where server returns plain text/HTML instead of JSON
        if (typeof responseData === 'string') {
          // Strip HTML tags if it's an HTML error page
          const cleanText = responseData.replace(/<[^>]*>/g, '').trim().substring(0, 300);
          errorMessage = cleanText || 'Server returned an unexpected error. Check server logs.';
        } else {
          errorMessage = responseData.error || responseData.detail || errorMessage;
        
          // Show validation errors if present
          if (responseData.validation_errors && responseData.validation_errors.length > 0) {
            errorMessage += '\n\nValidation Issues:\n• ' + responseData.validation_errors.join('\n• ');
          }
          
          // Show environment info for debugging
          if (responseData.environment) {
            errorMessage += `\n\nEnvironment: ${responseData.environment}`;
          }
          
          // Show API details if available
          if (responseData.details) {
            const details = responseData.details;
            const apiMessage = details.message || details.raw_response;
            if (apiMessage) {
              errorMessage += `\n\nAPI Message: ${apiMessage}`;
            }
            if (details.errors && typeof details.errors === 'object') {
              const fieldErrors = Object.entries(details.errors)
                .map(([key, val]) => `${key}: ${Array.isArray(val) ? val.join(', ') : val}`)
                .join('\n• ');
              if (fieldErrors) {
                errorMessage += `\n\nField Errors:\n• ${fieldErrors}`;
              }
            }
          }
        }
      } else {
        errorMessage += '\n\n' + (error.message || 'Network error. Please check your connection.');
      }
      
      alert(`❌ ${errorMessage}`);
    } finally {
      setSendingToSteadfast(null);
    }
  };

  // ── Bulk Send to Steadfast (create consignments) ──
  const handleBulkSendToSteadfast = async () => {
    // Selected orders that do NOT have consignment IDs yet (any status)
    const eligible = selectedRows.filter(o => !o.steadfast_consignment_id);
    if (eligible.length === 0) return;

    setBulkSending(true);
    try {
      const orderIds = eligible.map((o: any) => o.id);
      const response = await adminAPI.bulkSendToSteadfast(orderIds);
      const data = response.data;
      setBulkSendResult({ sent: data.sent, failed: data.failed, already_sent: data.already_sent ?? 0, results: data.results || [] });
      await loadOrders();
    } catch (error: any) {
      const msg = error?.response?.data?.error || error?.message || 'Unknown error';
      setBulkSendResult({ sent: 0, failed: eligible.length, already_sent: 0, results: eligible.map((o: any) => ({ order: o.order_number, success: false, message: msg })) });
    } finally {
      setBulkSending(false);
    }
  };

  // ── Pickup Request (physical pickup / manifest) ──
  const handleBulkPickupRequest = async () => {
    // Selected orders that HAVE consignment IDs (already sent to Steadfast)
    const eligible = selectedRows.filter(o => !!o.steadfast_consignment_id);
    if (eligible.length === 0) return;

    setPickupRequesting(true);
    try {
      const orderIds = eligible.map((o: any) => o.id);
      const response = await adminAPI.bulkPickupRequest(orderIds);
      const data = response.data;
      setPickupResult({
        requested: data.requested ?? 0,
        already_requested: data.already_requested ?? 0,
        total_cod: data.total_cod ?? 0,
        pickup_time: data.pickup_time || '',
        results: data.results || [],
        pickup_api_success: data.pickup_api_success,
        pickup_api_error: data.pickup_api_error,
        pickup_id: data.pickup_id,
      });
      await loadOrders();
    } catch (error: any) {
      const msg = error?.response?.data?.error || error?.message || 'Unknown error';
      setPickupResult({
        requested: 0, already_requested: 0, total_cod: 0, pickup_time: '',
        results: eligible.map((o: any) => ({ order: o.order_number, consignment_id: o.steadfast_consignment_id, already_requested: false, message: msg })),
      });
    } finally {
      setPickupRequesting(false);
    }
  };

  // Load customer fraud check data by phone
  const loadCustomerHistory = async (phone: string) => {
    if (!phone) {
      setCustomerHistory(null);
      return;
    }
    setCustomerHistoryLoading(true);
    setCustomerHistoryError('');
    try {
      const res = await adminAPI.getCustomerSteadfastHistory(phone);
      setCustomerHistory(res.data || null);
    } catch (err: any) {
      const msg = err?.response?.data?.error || '';
      if (msg && !msg.includes('not configured')) {
        setCustomerHistoryError(msg);
      }
    } finally {
      setCustomerHistoryLoading(false);
    }
  };

  const handleEditOrder = async (order: any) => {
    setEditingOrder(order);

    let matchedOptionId = order.delivery_option || null;
    if (!matchedOptionId && deliveryOptions.length > 0) {
      const orderShipCost = parseFloat(order.shipping_cost || 0);
      const matchByCost = deliveryOptions.find(opt => parseFloat(opt.cost) === orderShipCost);
      if (matchByCost) {
        matchedOptionId = matchByCost.id;
      } else {
        matchedOptionId = deliveryOptions[0].id;
      }
    }

    setEditForm({
      customer_name: order.customer_name || '',
      customer_phone: order.customer_phone || '',
      shipping_address: order.shipping_address || '',
      notes: order.notes || '',
      status: order.status || 'pending',
      shipping_cost: order.shipping_cost !== undefined && order.shipping_cost !== null ? String(order.shipping_cost) : '60',
      delivery_option: matchedOptionId,
    });
    setEditError('');
    setCustomerHistory(null);
    setCustomerHistoryError('');
    setEditModalTab('edit');
    // Reset discount form to current order values
    setDiscountType((order.admin_discount_type as 'flat' | 'percentage' | '') || '');
    setDiscountValue(order.admin_discount_value ? String(order.admin_discount_value) : '');
    setDiscountError('');
    setEditModalOpen(true);
    // Load site settings for packing slip
    try {
      const s = await websiteSettingsAPI.getSettings();
      setSiteSettings({ site_name: s.site_name || '', support_phone: s.support_phone || '', logo_url: s.logo_url || undefined });
    } catch {
      /* non-critical */ }
    // Load customer Steadfast history
    await loadCustomerHistory(order.customer_phone);
  };

  const handlePrintPackingSlip = () => {
    const content = packingSlipRef.current;
    if (!content) return;
    const printWindow = window.open('', '_blank', 'width=800,height=600');
    if (!printWindow) return;
    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
      <head>
        <title>Packing Slip - ${editingOrder?.order_number}</title>
        <style>
          @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800&display=swap');
          * { margin: 0; padding: 0; box-sizing: border-box; }
          body { font-family: 'Inter', Arial, sans-serif; font-size: 12px; color: #1e293b; background: white; }
          .slip { width: 100%; max-width: 780px; margin: 0 auto; }
          table { width: 100%; border-collapse: collapse; }
          th { background: #f1f5f9; font-weight: 700; font-size: 11px; text-transform: uppercase; letter-spacing: 0.05em; color: #475569; }
          th, td { padding: 8px 12px; }
          .slip-header { background: linear-gradient(135deg, #0f172a, #1e293b); color: white; padding: 16px 20px; display: flex; justify-content: space-between; align-items: center; }
          .slip-header .store-name { font-size: 16px; font-weight: 800; color: #f8fafc; }
          .slip-header .store-phone { font-size: 11px; color: #94a3b8; margin-top: 2px; }
          .slip-header .courier-label { font-size: 9px; color: #94a3b8; text-transform: uppercase; letter-spacing: 0.1em; text-align: right; }
          .slip-header .courier-name { font-size: 14px; font-weight: 800; color: #f8fafc; text-align: right; margin-top: 2px; }
          .info-row { display: flex; border-bottom: 1px solid #e2e8f0; }
          .ship-to { flex: 1; padding: 14px 18px; border-right: 1px solid #e2e8f0; }
          .order-info { width: 220px; padding: 14px 18px; background: #f8fafc; }
          .section-label { font-size: 9px; font-weight: 700; color: #94a3b8; text-transform: uppercase; letter-spacing: 0.1em; margin-bottom: 8px; }
          .recipient-name { font-size: 15px; font-weight: 800; color: #0f172a; margin-bottom: 4px; }
          .info-table td { padding: 3px 0; font-size: 11px; }
          .info-table .key { color: #64748b; font-weight: 600; padding-right: 10px; white-space: nowrap; }
          .info-table .val { font-weight: 700; color: #1e293b; }
          .items-table thead tr { border-bottom: 2px solid #e2e8f0; }
          .items-table tbody tr { border-bottom: 1px solid #f1f5f9; }
          .items-table tbody tr:nth-child(even) { background: #f8fafc; }
          .totals-wrap { display: flex; justify-content: flex-end; border-top: 1px solid #e2e8f0; }
          .totals-table { width: 260px; }
          .totals-table td { padding: 7px 14px; font-size: 12px; }
          .totals-table .lbl { color: #64748b; }
          .cod-row td { background: linear-gradient(135deg, #0f172a, #1e293b) !important; color: white !important; font-weight: 800; font-size: 14px; padding: 11px 14px; }
          .slip-footer { padding: 10px 18px; background: #f8fafc; border-top: 1px solid #e2e8f0; font-size: 10px; color: #94a3b8; text-align: center; }
          @page { margin: 10mm; size: A4; }
          @media print { body { -webkit-print-color-adjust: exact; print-color-adjust: exact; } }
        </style>
      </head>
      <body>
        <div class="slip">${content.outerHTML}</div>
      </body>
      </html>
    `);
    printWindow.document.close();
    printWindow.focus();
    setTimeout(() => { printWindow.print(); printWindow.close(); }, 400);
  };

  const closeEditModal = () => {
    setEditModalOpen(false);
    setEditingOrder(null);
    setEditError('');
  };

  const handleEditFormChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setEditForm(prev => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleApplyDiscount = async (remove = false) => {
    if (!editingOrder) return;
    setDiscountSaving(true);
    setDiscountError('');
    try {
      const type = remove ? null : discountType || null;
      const value = remove ? null : (discountValue ? parseFloat(discountValue) : null);
      const res = await adminAPI.applyOrderDiscount(editingOrder.id, type, value);
      const updated = res.data;
      // Patch the local orders array & editingOrder with fresh data
      setOrders(prev => prev.map(o => o.id === updated.id ? { ...o, ...updated } : o));
      setEditingOrder((prev: any) => ({ ...prev, ...updated }));
      if (remove) {
        setDiscountType('');
        setDiscountValue('');
      }
    } catch (err: any) {
      setDiscountError(err?.response?.data?.error || err?.message || 'Failed to apply discount');
    } finally {
      setDiscountSaving(false);
    }
  };

  const handleSaveEdit = async () => {
    if (!editingOrder) return;
    setEditSaving(true);
    setEditError('');
    try {
      const statusChanged = editForm.status !== editingOrder.status;
      // Update core fields via PATCH
      await adminAPI.updateOrder(editingOrder.id, {
        customer_name: editForm.customer_name,
        customer_phone: editForm.customer_phone,
        shipping_address: editForm.shipping_address,
        notes: editForm.notes,
        shipping_cost: parseFloat(editForm.shipping_cost) || 0,
        delivery_option: editForm.delivery_option || null,
      });
      // Update status via dedicated endpoint (triggers email notification)
      if (statusChanged) {
        await adminAPI.updateOrderStatus(editingOrder.id, editForm.status);
      }
      closeEditModal();
      await loadOrders();
    } catch (error: any) {
      console.error('Failed to update order:', error);
      setEditError(error.response?.data?.error || error.message || 'Failed to save changes');
    } finally {
      setEditSaving(false);
    }
  };

  const handleViewOrder = (order: any) => {
    setSelectedOrder(order);
    setViewModalOpen(true);
  };

  const closeViewModal = () => {
    setViewModalOpen(false);
    setSelectedOrder(null);
  };

  const handleDeleteOrder = async (orderId: number) => {
    if (!window.confirm('Are you sure you want to delete this order? This action cannot be undone.')) {
      return;
    }

    try {
      // TODO: Implement delete API endpoint
      // await adminAPI.deleteOrder(orderId);
      alert('Delete functionality will be implemented soon.');
      // await loadOrders();
    } catch (error: any) {
      console.error('Failed to delete order:', error);
      alert(`Error: ${error.response?.data?.error || error.message || 'Failed to delete order'}`);
    }
  };

  // ── Print Single Label ──
  const handlePrintSingleLabel = async (orderId: number) => {
    setPrintingLabel(orderId);
    try {
      const res = await adminAPI.generateLabels([orderId]);
      const blob = new Blob([res.data], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      const printWin = window.open(url, '_blank');
      if (printWin) {
        printWin.addEventListener('load', () => {
          setTimeout(() => { try { printWin.print(); } catch {} }, 600);
        });
      }
    } catch (error: any) {
      console.error('Failed to print label:', error);
      alert(`Failed to generate label: ${error?.response?.data?.error || error?.message || 'Unknown error'}`);
    } finally {
      setPrintingLabel(null);
    }
  };

  // ── Bulk Print Labels ──
  const handleBulkPrintLabels = async () => {
    if (selectedRows.length === 0) return;
    setBulkPrinting(true);
    try {
      const orderIds = selectedRows.map((o: any) => o.id);
      const res = await adminAPI.generateLabels(orderIds);
      const blob = new Blob([res.data], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      const printWin = window.open(url, '_blank');
      if (printWin) {
        printWin.addEventListener('load', () => {
          setTimeout(() => { try { printWin.print(); } catch {} }, 600);
        });
      }
    } catch (error: any) {
      console.error('Failed to bulk print labels:', error);
      alert(`Failed to generate labels: ${error?.response?.data?.error || error?.message || 'Unknown error'}`);
    } finally {
      setBulkPrinting(false);
    }
  };

  // ── Bulk Category Assignment ──
  // Category → status mapping (same as bulk)
  const CATEGORY_STATUS_MAP: Record<string, string> = {
    new:            'pending',
    future:         'preorder',
    not_response:   'no_response',
    confirmed:      'processing',
    confirmed_sent: 'processing',
    cancelled:      'cancelled',
  };

  // Status → category mapping (reverse) — used as fallback when no steadfast_consignment_id
  const STATUS_CATEGORY_MAP: Record<string, string> = {
    preorder:   'future',
    pending:    'new',
    no_response:'not_response',
    processing: 'confirmed',
    shipped:    'confirmed',
    delivered:  'confirmed',
    cancelled:  'cancelled',
  };

  // Smart category resolver — 'confirmed_sent' takes priority when courier consignment exists
  const getOrderCategory = (order: any): string => {
    if (order.steadfast_consignment_id) return 'confirmed_sent';
    return STATUS_CATEGORY_MAP[order.status?.toLowerCase()] || '';
  };

  const CATEGORY_LABELS: Record<string, { label: string; color: string; bg: string; border: string }> = {
    new:            { label: '🆕 New',                color: '#1d4ed8', bg: '#dbeafe', border: '#93c5fd' },
    future:         { label: '📋 Future',             color: '#7c3aed', bg: '#ede9fe', border: '#c4b5fd' },
    not_response:   { label: '📞 Not Response',       color: '#b45309', bg: '#fef3c7', border: '#fcd34d' },
    confirmed:      { label: '✅ Confirmed',           color: '#065f46', bg: '#d1fae5', border: '#6ee7b7' },
    confirmed_sent: { label: '🚚 Confirmed & Sent',   color: '#0f4c75', bg: '#e0f2fe', border: '#7dd3fc' },
    cancelled:      { label: '❌ Cancelled',           color: '#991b1b', bg: '#fee2e2', border: '#fca5a5' },
  };

  const handleCategoryChange = async (orderId: number, category: string) => {
    const targetStatus = CATEGORY_STATUS_MAP[category];
    if (!targetStatus) return;
    setCategoryUpdating(prev => new Set(prev).add(orderId));
    try {
      await adminAPI.updateOrderStatus(orderId, targetStatus);
      setOrders(prev => prev.map(o =>
        o.id === orderId ? { ...o, status: targetStatus } : o
      ));
    } catch (err: any) {
      console.error('Failed to update category:', err);
    } finally {
      setCategoryUpdating(prev => {
        const next = new Set(prev);
        next.delete(orderId);
        return next;
      });
    }
  };

  const handleBulkCategoryAssign = async () => {
    if (!assignCategory || selectedRows.length === 0) return;
    const statusMap: Record<string, string> = {
      new:            'pending',
      future:         'preorder',
      not_response:   'no_response',
      confirmed:      'processing',
      confirmed_sent: 'processing',
      cancelled:      'cancelled',
    };
    const targetStatus = statusMap[assignCategory];
    if (!targetStatus) return;
    const categoryLabelMap: Record<string, string> = { new: 'New', not_response: 'Not Response', confirmed_sent: 'Confirmed & Sent' };
    const categoryLabel = categoryLabelMap[assignCategory] ?? assignCategory.charAt(0).toUpperCase() + assignCategory.slice(1);
    if (!window.confirm(`Move ${selectedRows.length} order(s) to "${categoryLabel}"?`)) return;
    setBulkAssigning(true);
    try {
      await Promise.all(selectedRows.map(order => adminAPI.updateOrderStatus(order.id, targetStatus)));
      await loadOrders();
      setAssignCategory('');
    } catch (error: any) {
      console.error('Failed to assign category:', error);
      alert(`Error: ${error.response?.data?.error || error.message || 'Failed to assign category'}`);
    } finally {
      setBulkAssigning(false);
    }
  };

  const handleBulkDelete = async () => {
    if (selectedRows.length === 0) {
      alert('Please select orders to delete');
      return;
    }

    if (!window.confirm(`Are you sure you want to delete ${selectedRows.length} selected order(s)? This action cannot be undone.`)) {
      return;
    }

    try {
      // TODO: Implement bulk delete
      alert('Bulk delete functionality will be implemented soon.');
    } catch (error: any) {
      console.error('Failed to bulk delete orders:', error);
      alert(`Error: ${error.message || 'Failed to delete orders'}`);
    }
  };

  // ── Sync Steadfast Statuses (bulk) ──
  const handleSyncSteadfastStatuses = async (silent = false) => {
    if (syncing) return;
    setSyncing(true);
    try {
      const res = await adminAPI.syncSteadfastStatuses();
      const data = res.data;
      const changed = (data.results || []).filter((r: any) => r.changed).length;
      setLastSyncTime(new Date().toLocaleTimeString());
      setSyncStats({ synced: data.synced || 0, changed });
      // Always reload orders to reflect current state
      await loadOrders();
    } catch (err: any) {
      if (!silent) {
        console.error('Sync failed:', err);
      }
    } finally {
      setSyncing(false);
    }
  };

  const handleViewTracking = async (orderId: number, consignmentId: string) => {
    try {
      const response = await adminAPI.getSteadfastTracking(orderId);
      const data = response.data;
      
      if (data.success) {
        // Refresh this order in the grid
        const response2 = await adminAPI.getOrders();
        const d = response2.data || response2;
        const ordersList = Array.isArray(d) ? d : (d.results || []);
        setOrders(ordersList);

        const trackingInfo = data.tracking_data;
        const sfStatus = data.steadfast_status || (typeof trackingInfo === 'string' ? trackingInfo : trackingInfo?.delivery_status) || 'Unknown';
        alert(`Steadfast Status: ${sfStatus.replace(/_/g, ' ')}\n\nConsignment: ${data.consignment_id}\nTracking: ${data.tracking_code}`);
      } else {
        alert(`Failed to get tracking info: ${data.error}`);
      }
    } catch (error: any) {
      console.error('Failed to get tracking info:', error);
      alert(`Error: ${error.response?.data?.error || error.message || 'Failed to get tracking info'}`);
    }
  };

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
  };

  // Ensure orders is always an array before filtering
  const ordersArray = Array.isArray(orders) ? orders : [];

  // Count per group for badges
  const groupCounts: Record<string, number> = { all: ordersArray.length };
  Object.entries(STATUS_GROUPS).forEach(([group, statuses]) => {
    if (group === 'confirmed') {
      // confirmed = processing/shipped/delivered WITHOUT courier consignment
      groupCounts[group] = ordersArray.filter(o =>
        statuses.includes(o.status?.toLowerCase()) && !o.steadfast_consignment_id
      ).length;
    } else {
      groupCounts[group] = ordersArray.filter(o => statuses.includes(o.status?.toLowerCase())).length;
    }
  });
  // confirmed_sent = any order that has been sent to courier
  groupCounts['confirmed_sent'] = ordersArray.filter(o => !!o.steadfast_consignment_id).length;

  const filteredOrders = ordersArray.filter(order => {
    const matchesSearch = order.order_number?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      order.customer_name?.toLowerCase().includes(searchTerm.toLowerCase());
    let matchesStatus = true;
    if (statusFilter === 'confirmed_sent') {
      matchesStatus = !!order.steadfast_consignment_id;
    } else if (statusFilter === 'confirmed') {
      matchesStatus = ['processing', 'shipped', 'delivered'].includes(order.status?.toLowerCase()) &&
        !order.steadfast_consignment_id;
    } else if (statusFilter !== 'all') {
      const group = STATUS_GROUPS[statusFilter];
      if (group) {
        // group filter
        matchesStatus = group.includes(order.status?.toLowerCase());
      } else {
        // individual status filter
        matchesStatus = order.status?.toLowerCase() === statusFilter;
      }
    }
    
    // Date range filtering
    let matchesDate = true;
    if (startDate || endDate) {
      const orderDate = new Date(order.created_at);
      if (startDate) {
        const start = new Date(startDate);
        start.setHours(0, 0, 0, 0);
        if (orderDate < start) matchesDate = false;
      }
      if (endDate) {
        const end = new Date(endDate);
        end.setHours(23, 59, 59, 999);
        if (orderDate > end) matchesDate = false;
      }
    }
    
    return matchesSearch && matchesStatus && matchesDate;
  });

  const handleExportCSV = () => {
    const escapeCSV = (val: any): string => {
      if (val === null || val === undefined) return '';
      const str = String(val);
      if (str.includes(',') || str.includes('"') || str.includes('\n')) {
        return '"' + str.replace(/"/g, '""') + '"';
      }
      return str;
    };

    const headers = [
      'Order #',
      'Order Date',
      'Product Title',
      'Color',
      'Size',
      'Weight',
      'Quantity',
      'Customer Name',
      'Phone',
      'Address',
      'Notes',
      'Category',
      'Total Amount',
      'Shipping Cost',
      'Status',
      'Steadfast Tracking',
      'Steadfast Consignment',
      'Steadfast Status',
    ];

    const rows = filteredOrders.map(order => {
      const firstItem = order.items?.[0];
      const productTitle = (() => {
        const items = order.items || [];
        if (items.length === 0) return 'No items';
        if (items.length === 1) return items[0].product_name || 'Unknown';
        return `${items[0].product_name || 'Unknown'} (+${items.length - 1} more)`;
      })();
      const orderDate = order.created_at
        ? new Date(order.created_at).toLocaleString('en-US', { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })
        : '';
      return [
        escapeCSV(order.order_number),
        escapeCSV(orderDate),
        escapeCSV(productTitle),
        escapeCSV(firstItem?.selected_color || firstItem?.color || ''),
        escapeCSV(firstItem?.selected_size || firstItem?.size || ''),
        escapeCSV(firstItem?.selected_weight || firstItem?.weight || ''),
        escapeCSV(firstItem?.quantity || 1),
        escapeCSV(order.customer_name),
        escapeCSV(order.customer_phone),
        escapeCSV(order.shipping_address),
        escapeCSV(order.notes),
        escapeCSV(CATEGORY_LABELS[getOrderCategory(order) as keyof typeof CATEGORY_LABELS]?.label || ''),
        escapeCSV(parseFloat(order.total_amount || 0).toFixed(2)),
        escapeCSV(parseFloat(order.shipping_cost || 0).toFixed(2)),
        escapeCSV(order.status),
        escapeCSV(order.steadfast_tracking_code || ''),
        escapeCSV(order.steadfast_consignment_id || ''),
        escapeCSV(order.steadfast_status || ''),
      ].join(',');
    });

    const csv = [headers.join(','), ...rows].join('\n');
    const blob = new Blob(['\uFEFF' + csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    const today = new Date().toISOString().slice(0, 10);
    const filterLabel = statusFilter !== 'all' ? `_${statusFilter}` : '';
    a.href = url;
    a.download = `orders${filterLabel}_${today}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  // ── Sort + Paginate ──


  const sortedOrders = useMemo(() => {
    return [...filteredOrders].sort((a: any, b: any) => {
      let va: any, vb: any;
      if (sortField === 'product_title') {
        va = (a.items?.[0]?.product_name || '').toLowerCase();
        vb = (b.items?.[0]?.product_name || '').toLowerCase();
      } else {
        va = (a[sortField] ?? '');
        vb = (b[sortField] ?? '');
      }
      const cmp = va < vb ? -1 : va > vb ? 1 : 0;
      return sortDir === 'asc' ? cmp : -cmp;
    });
  }, [filteredOrders, sortField, sortDir]);

  const totalPages = Math.max(1, Math.ceil(sortedOrders.length / pageSize));
  const safeCurrentPage = Math.min(currentPage, totalPages);
  const startIndex = (safeCurrentPage - 1) * pageSize;
  const endIndex = Math.min(startIndex + pageSize, sortedOrders.length);
  const pagedOrders = sortedOrders.slice(startIndex, endIndex);

  const selectedIds = new Set(selectedRows.map((o: any) => o.id));

  const handleSelectRow = (order: any, checked: boolean) => {
    if (checked) setSelectedRows((prev: any[]) => [...prev, order]);
    else setSelectedRows((prev: any[]) => prev.filter((o: any) => o.id !== order.id));
  };

  const handleSelectAll = (checked: boolean) => {
    if (checked) setSelectedRows(pagedOrders);
    else setSelectedRows([]);
  };

  return (
    <div className="admin-page om-page">

      {/* ── Top Action Bar ── */}
      <div className="om-topbar">
        <h1 className="om-page-title">Order Management</h1>
        <div className="om-topbar-right">
          <button className="om-btn om-btn-csv" onClick={handleExportCSV}>
            <Download size={13} /> EXPORT CSV <span className="om-btn-badge">{filteredOrders.length}</span>
          </button>
          <button
            className="om-btn om-btn-manual"
            onClick={() => { setManualModalOpen(true); setManualError(''); setManualSuccess(''); }}
          >
            <Phone size={13} /> New Order
          </button>
          <button
            className="om-btn om-btn-sync"
            onClick={() => handleSyncSteadfastStatuses(false)}
            disabled={syncing}
            title={lastSyncTime ? `Last synced: ${lastSyncTime}` : 'Sync Steadfast delivery statuses'}
          >
            {syncing
              ? <><span className="om-spin"><Loader2 size={13} /></span> Syncing</>
              : <><RefreshCw size={13} /> Sync</>
            }
            {syncStats && !syncing && (
              <span className="om-btn-badge">{syncStats.changed > 0 ? `${syncStats.changed}↑` : <CheckCircle2 size={10} />}</span>
            )}
          </button>

          {/* Bulk: Send to Steadfast */}
          {selectedRows.filter((o: any) => !o.steadfast_consignment_id).length > 0 && (
            <button className="om-btn om-btn-sf" onClick={handleBulkSendToSteadfast} disabled={bulkSending}>
              {bulkSending ? <span className="om-spin"><Loader2 size={13} /></span> : <Package size={13} />} Send to SF
              <span className="om-btn-badge">{selectedRows.filter((o: any) => !o.steadfast_consignment_id).length}</span>
            </button>
          )}
          {/* Bulk: Pickup */}
          {selectedRows.filter((o: any) => !!o.steadfast_consignment_id).length > 0 && (
            <button className="om-btn om-btn-pickup" onClick={handleBulkPickupRequest} disabled={pickupRequesting}>
              {pickupRequesting ? <span className="om-spin"><Loader2 size={13} /></span> : <Truck size={13} />} Pickup
              <span className="om-btn-badge">{selectedRows.filter((o: any) => !!o.steadfast_consignment_id).length}</span>
            </button>
          )}
          {/* Bulk: Print Labels */}
          {selectedRows.length > 0 && (
            <button className="om-btn om-btn-print" onClick={handleBulkPrintLabels} disabled={bulkPrinting}>
              {bulkPrinting ? <span className="om-spin"><Loader2 size={13} /></span> : <Printer size={13} />} Print
              <span className="om-btn-badge">{selectedRows.length}</span>
            </button>
          )}
          {/* Bulk: Assign Category */}
          {selectedRows.length > 0 && (
            <>
              <select value={assignCategory} onChange={(e) => setAssignCategory(e.target.value)} className="om-cat-select">
                <option value="">Move to…</option>
                <option value="new">New</option>
                <option value="future">Future</option>
                <option value="not_response">Not Response</option>
                <option value="confirmed">Confirmed</option>
                <option value="confirmed_sent">Conf. &amp; Sent</option>
                <option value="cancelled">Cancelled</option>
              </select>
              {assignCategory && (
                <button className="om-btn om-btn-save" onClick={handleBulkCategoryAssign} disabled={bulkAssigning}>
                  {bulkAssigning ? <span className="om-spin"><Loader2 size={13} /></span> : <Save size={13} />} Save
                  <span className="om-btn-badge">{selectedRows.length}</span>
                </button>
              )}
              <button className="om-btn om-btn-del" onClick={handleBulkDelete}>
                <Trash2 size={13} /> {selectedRows.length}
              </button>
            </>
          )}
        </div>
      </div>

      {/* ── Filter Chips ── */}
      <div className="om-filter-wrap">
        <div className="om-filter-status">
          <select 
            className="om-status-filter-select"
            value={statusFilter}
            onChange={(e) => { setStatusFilter(e.target.value as StatusFilter); setCurrentPage(1); }}
          >
            {([
              { key: 'all',            label: 'All',               count: ordersArray.length },
              { key: 'new',            label: 'New Orders',        count: groupCounts.new ?? 0 },
              { key: 'confirmed',      label: 'Confirmed',         count: groupCounts.confirmed ?? 0 },
              { key: 'confirmed_sent', label: 'Ready To Delivery', count: groupCounts.confirmed_sent ?? 0 },
              { key: 'not_response',   label: 'Not Response',      count: groupCounts.not_response ?? 0 },
              { key: 'cancelled',      label: 'Cancelled',         count: groupCounts.cancelled ?? 0 },
              { key: 'future',         label: 'Future',            count: groupCounts.future ?? 0 },
            ] as { key: string; label: string; count: number }[]).map(chip => (
              <option key={chip.key} value={chip.key}>
                {chip.label} : {chip.count}
              </option>
            ))}
          </select>
        </div>

        <div className="om-filter-row2">
          <div className="om-date-range">
            <input
              type="date" value={startDate}
              onChange={e => { setStartDate(e.target.value); setCurrentPage(1); }}
              className="om-date-input" title="From date"
            />
            <span className="om-date-sep">to</span>
            <input
              type="date" value={endDate}
              onChange={e => { setEndDate(e.target.value); setCurrentPage(1); }}
              className="om-date-input" title="To date"
            />
            {(startDate || endDate) && (
              <button onClick={() => { setStartDate(''); setEndDate(''); }} className="om-date-clear"><X size={11} /></button>
            )}
          </div>
          <div className="om-search-wrap">
            <span className="om-search-icon"><Search size={13} /></span>
            <input
              type="text"
              placeholder="Search orders, IDs, customers..."
              value={searchTerm}
              onChange={handleSearch}
              className="om-search-input"
            />
          </div>
        </div>
      </div>

      {/* ── MOBILE CARD LIST ── */}
      {isMobile && (
        <div className="mobile-card-list">
          {loading && <div className="mc-loading">Loading orders…</div>}
          {!loading && filteredOrders.length === 0 && <div className="mc-empty">No orders found.</div>}
          {!loading && filteredOrders.map((order) => {
            const firstItem = order.items?.[0] || {};
            const imgUrl = firstItem.product_image || firstItem.image || '';
            const productName = firstItem.product_name || firstItem.name || 'Unknown Product';
            const extraItems = (order.items?.length || 1) - 1;
            const statusKey = (order.status || 'pending').toLowerCase();
            const statusClass = statusKey.replace(/_/g, '-');
            const statusLabel = (order.status || 'Pending').replace(/_/g, ' ');
            const color  = firstItem.selected_color  || '';
            const size   = firstItem.selected_size   || '';
            const weight = firstItem.selected_weight || '';
            const isSending  = sendingToSteadfast === order.id;
            const isPrinting = printingLabel === order.id;
            const currentCategory = getOrderCategory(order);
            const categoryConfig  = CATEGORY_LABELS[currentCategory];
            return (
              <div className="mobile-card" key={order.id}>
                <div className="mc-top">
                  {imgUrl ? (
                    <img className="mc-thumb" src={imgUrl} alt="Product" />
                  ) : (
                    <div className="mc-thumb-placeholder">📦</div>
                  )}
                  <div className="mc-info">
                    <div className="mc-title">{order.order_number}</div>
                    <div className="mc-subtitle">{productName}{extraItems > 0 ? ` (+${extraItems} more)` : ''}</div>
                  </div>
                  <div className="mc-badge-corner">
                    <span className={`status-badge ${statusClass}`}>{statusLabel}</span>
                  </div>
                </div>
                {(color || size || weight) && (
                  <div className="mc-variants">
                    {color && <span className="mc-variant-pill" style={{ background: '#f5f3ff', color: '#7c3aed', borderColor: '#ddd6fe' }}><span className="mc-variant-color-dot" style={{ background: color.toLowerCase() }} />{color}</span>}
                    {size && <span className="mc-variant-pill" style={{ background: '#EEF2FF', color: '#4F46E5', borderColor: '#C7D2FE' }}>{size}</span>}
                    {weight && <span className="mc-variant-pill" style={{ background: '#ECFDF5', color: '#059669', borderColor: '#A7F3D0' }}>{weight}</span>}
                  </div>
                )}
                <div className="mc-divider" />
                <div className="mc-meta">
                  <div className="mc-meta-item"><span className="mc-meta-label">👤</span><span className="mc-meta-value">{order.customer_name || '—'}</span></div>
                  <div className="mc-meta-item"><span className="mc-meta-label">📱</span><span className="mc-meta-value">{order.customer_phone || '—'}</span></div>
                  <div className="mc-meta-item"><span className="mc-meta-label">💰</span>
                    <span className="mc-meta-value">
                      {parseFloat(order.discount_amount || 0) > 0
                        ? <>৳{parseFloat(order.final_amount || order.total_amount || 0).toFixed(0)} <span style={{ textDecoration: 'line-through', color: '#9ca3af', fontSize: '10px' }}>৳{parseFloat(order.total_amount || 0).toFixed(0)}</span></>
                        : `৳${parseFloat(order.total_amount || 0).toFixed(0)}`
                      }
                    </span>
                  </div>
                  <div className="mc-meta-item"><span className="mc-meta-label">📅</span><span className="mc-meta-value">{order.created_at ? new Date(order.created_at).toLocaleDateString('en-GB', { day: '2-digit', month: 'short' }) : '—'}</span></div>
                </div>
                {order.shipping_address && <div className="mc-address">📍 {order.shipping_address}</div>}
                <div style={{ marginTop: '8px' }}>
                  <select
                    className="mc-status-select"
                    value={currentCategory}
                    onChange={(e) => handleCategoryChange(order.id, e.target.value)}
                    disabled={categoryUpdating.has(order.id)}
                    style={{ borderColor: categoryConfig?.border || '#e5e7eb', background: categoryConfig?.bg || '#f9fafb', color: categoryConfig?.color || '#374151' }}
                  >
                    <option value="">— Category —</option>
                    <option value="new">New</option>
                    <option value="future">Future</option>
                    <option value="not_response">Not Response</option>
                    <option value="confirmed">Confirmed</option>
                    <option value="confirmed_sent">Conf. &amp; Sent</option>
                    <option value="cancelled">Cancelled</option>
                  </select>
                </div>
                <div className="mc-actions">
                  <button className="mc-btn mc-btn-view" onClick={() => handleViewOrder(order)}>View</button>
                  <button className="mc-btn mc-btn-edit" onClick={() => handleEditOrder(order)}>Edit</button>
                  <button className="mc-btn mc-btn-print" onClick={() => handlePrintSingleLabel(order.id)} disabled={isPrinting}>{isPrinting ? 'Printing…' : 'Print'}</button>

                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* ── DESKTOP TABLE ── */}
      {!isMobile && (
        <div className="om-table-card">
          <div className="om-table-scroll">
            <table className="om-table">
              <colgroup>
                <col className="col-check" />
                <col className="col-product" />
                <col className="col-order" />
                <col className="col-customer" />
                <col className="col-address" />
                <col className="col-category" />
                <col className="col-variant" />
                <col className="col-finance" />
                <col className="col-status" />
                <col className="col-sf" />
                <col className="col-notes" />
                <col className="col-actions" />
              </colgroup>
              <thead>
                <tr>
                  <th className="om-th om-th-check">
                    <input
                      type="checkbox"
                      className="om-checkbox"
                      checked={pagedOrders.length > 0 && selectedIds.size === pagedOrders.length}
                      onChange={e => handleSelectAll(e.target.checked)}
                    />
                  </th>
                  {([
                    { key: 'product',          label: 'Product',    sortKey: 'product_title' },
                    { key: 'order_number',     label: 'Order #',    sortKey: 'order_number' },
                    { key: 'customer',         label: 'Customer',   sortKey: 'customer_name' },
                    { key: 'shipping_address', label: 'Address',    sortKey: 'shipping_address' },
                    { key: 'category',         label: 'Category',   sortKey: '' },
                    { key: 'variant',          label: 'Variant',    sortKey: '' },
                    { key: 'total_amount',     label: 'Financials', sortKey: 'total_amount' },
                    { key: 'status',           label: 'Status',     sortKey: 'status' },
                    { key: 'steadfast',        label: 'Steadfast',  sortKey: '' },
                    { key: 'notes',            label: 'Notes',      sortKey: '' },
                    { key: 'actions',          label: 'Actions',    sortKey: '' },
                  ] as { key: string; label: string; sortKey: string }[]).map(col => (
                    <th
                      key={col.key}
                      className={`om-th${col.sortKey ? ' om-th-sort' : ''}`}
                      onClick={() => {
                        if (!col.sortKey) return;
                        if (sortField === col.sortKey) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
                        else { setSortField(col.sortKey); setSortDir('asc'); }
                        setCurrentPage(1);
                      }}
                    >
                      {col.label}
                      {sortField === col.sortKey && col.sortKey
                        ? <span className="om-sort-arrow">{sortDir === 'asc' ? <ChevronUp size={11} /> : <ChevronDown size={11} />}</span>
                        : null}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {loading && (
                  <tr><td colSpan={11} className="om-loading">Loading orders…</td></tr>
                )}
                {!loading && pagedOrders.length === 0 && (
                  <tr><td colSpan={11} className="om-empty">No orders found.</td></tr>
                )}
                {!loading && pagedOrders.map(order => {
                  const firstItem = order.items?.[0] || {};
                  const imgUrl = firstItem.product_image || firstItem.image || '';
                  const productName = firstItem.product_name || firstItem.name || 'Unknown Product';
                  const sku = firstItem.sku || '';
                  const extraItems = (order.items?.length || 1) - 1;
                  const color  = firstItem.selected_color  || '';
                  const size   = firstItem.selected_size   || '';
                  const weight = firstItem.selected_weight || '';
                  const total    = parseFloat(order.total_amount    || 0);
                  const discount = parseFloat(order.discount_amount || 0);
                  const final    = parseFloat(order.final_amount    || total);
                  const shipping = parseFloat(order.shipping_cost   || 0);
                  const isSelected  = selectedIds.has(order.id);
                  const isSending   = sendingToSteadfast === order.id;
                  const isPrinting  = printingLabel === order.id;
                  const currentCategory = getOrderCategory(order);
                  const catCfg = CATEGORY_LABELS[currentCategory];

                  return (
                    <tr key={order.id} className={`om-row${isSelected ? ' om-row-selected' : ''}`}>

                      {/* ── Checkbox ── */}
                      <td className="om-td om-td-check">
                        <input
                          type="checkbox"
                          className="om-checkbox"
                          checked={isSelected}
                          onChange={e => handleSelectRow(order, e.target.checked)}
                        />
                      </td>

                      {/* ── Product ── */}
                      <td className="om-td om-td-product">
                        <div className="om-product-cell">
                          {imgUrl
                            ? <img src={imgUrl} alt="" className="om-product-img" />
                            : <div className="om-product-img-placeholder"><Box size={16} /></div>
                          }
                          <div className="om-product-info">
                            <span className="om-product-name">
                              {productName}{extraItems > 0 ? ` (+${extraItems})` : ''}
                            </span>
                            {sku && <span className="om-product-sku">SKU: {sku}</span>}
                            {order.source === 'manual' ? (
                              <span className="om-badge om-badge-manual"><Phone size={8} /> MANUAL</span>
                            ) : (
                              <span className="om-badge om-badge-landing">LANDING PAGE</span>
                            )}
                          </div>
                        </div>
                      </td>

                      {/* ── Order # ── */}
                      <td className="om-td">
                        <div className="om-order-num">{order.order_number}</div>
                        <div className="om-order-date">
                          {order.created_at
                            ? new Date(order.created_at).toLocaleString('en-GB', {
                                day: '2-digit', month: 'short', year: 'numeric',
                                hour: '2-digit', minute: '2-digit'
                              })
                            : '—'}
                        </div>
                      </td>

                      {/* ── Customer ── */}
                      <td className="om-td">
                        <div style={{ fontWeight: '600', color: '#1e293b', fontSize: '0.82rem', marginBottom: '1px' }}>{order.customer_name || '—'}</div>
                        <div style={{ color: '#2563eb', fontSize: '0.75rem', fontWeight: '500' }}>{order.customer_phone || '—'}</div>
                      </td>

                      {/* ── Address ── */}
                      <td className="om-td om-td-address">
                        <div className="om-address-text" style={{ lineHeight: '1.3', fontSize: '0.75rem' }}>{order.shipping_address || '—'}</div>
                      </td>

                      {/* ── Category ── */}
                      <td className="om-td">
                        {categoryUpdating.has(order.id) ? (
                          <span className="om-saving">Saving…</span>
                        ) : (
                          <select
                            value={currentCategory}
                            onChange={e => handleCategoryChange(order.id, e.target.value)}
                            className="om-cat-dropdown"
                            style={{
                              background: catCfg?.bg || '#f9fafb',
                              color: catCfg?.color || '#374151',
                              borderColor: catCfg?.border || '#e5e7eb',
                            }}
                          >
                            <option value="">— Select —</option>
                            <option value="new">New</option>
                            <option value="future">Future</option>
                            <option value="not_response">Not Response</option>
                            <option value="confirmed">Confirmed</option>
                            <option value="confirmed_sent">Conf. &amp; Sent</option>
                            <option value="cancelled">Cancelled</option>
                          </select>
                        )}
                      </td>

                      {/* ── Variant ── */}
                      <td className="om-td om-td-variant">
                        <div className="om-variant-stack">
                          {color && (
                            <span className="om-chip-var om-chip-color">
                              <span className="om-color-dot" style={{ background: color.toLowerCase() }} />
                              {color}
                            </span>
                          )}
                          {size   && <span className="om-chip-var om-chip-size">{size}</span>}
                          {weight && <span className="om-chip-var om-chip-weight">{weight}</span>}
                          {!color && !size && !weight && <span className="om-no-variant">—</span>}
                        </div>
                      </td>

                      {/* ── Financials ── */}
                      <td className="om-td om-td-finance">
                        {discount > 0 ? (
                          <>
                            <span className="om-price-final">৳{final.toFixed(2)}</span>
                            <span className="om-price-orig">৳{total.toFixed(2)}</span>
                            <span className="om-price-ship">Shipping: ৳{shipping.toFixed(2)}</span>
                          </>
                        ) : (
                          <>
                            <span className="om-price-main">৳{total.toFixed(2)}</span>
                            <span className="om-price-ship">Shipping: ৳{shipping.toFixed(2)}</span>
                          </>
                        )}
                      </td>

                      {/* ── Status ── */}
                      <td className="om-td">
                        <span className={`om-status-chip om-status-${(order.status || 'pending').toLowerCase().replace(/_/g, '-')}`}>
                          {(order.status || 'Pending').replace(/_/g, ' ')}
                        </span>
                      </td>

                      {/* ── Steadfast ── */}
                      <td className="om-td om-td-sf">
                        {order.steadfast_tracking_code ? (
                          <>
                            <span className="om-sf-label">Courier tracking&</span>
                            <span className="om-sf-consignment">
                              Consignment ID: {order.steadfast_consignment_id || '—'}
                            </span>
                          </>
                        ) : (
                          <span className="om-no-variant">—</span>
                        )}
                      </td>

                      {/* ── Notes ── */}
                      <td className="om-td om-td-notes">
                        <span className="om-notes-text">{order.notes || '—'}</span>
                      </td>

                      {/* ── Actions ── */}
                      <td className="om-td om-td-actions">
                        <div className="om-actions-row">
                          <button
                            className="om-action-btn om-action-print"
                            title="Print Label"
                            onClick={() => handlePrintSingleLabel(order.id)}
                            disabled={isPrinting}
                          >
                            {isPrinting ? <span className="om-spin"><Loader2 size={12} /></span> : <Printer size={12} />}
                          </button>
                          <button
                            className="om-action-btn om-action-edit"
                            title="Edit Order"
                            onClick={() => handleEditOrder(order)}
                          >
                            <Pencil size={12} />
                          </button>
                          {order.steadfast_consignment_id && (
                            <button
                              className="om-action-btn om-action-track"
                              title="Track"
                              onClick={() => handleViewTracking(order.id, order.steadfast_consignment_id)}
                            >
                              <MapPin size={12} />
                            </button>
                          )}
                          <button
                            className="om-action-btn om-action-view"
                            title="View Details"
                            onClick={() => handleViewOrder(order)}
                          >
                            <Eye size={12} />
                          </button>
                          <button
                            className="om-action-btn om-action-del"
                            title="Delete Order"
                            onClick={() => handleDeleteOrder(order.id)}
                          >
                            <Trash2 size={12} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

        </div>
      )}

      {/* ── Pagination (Shared for Mobile & Desktop) ── */}
      {sortedOrders.length > 0 && (
        <div className="om-pagination">
          <div className="om-page-size-selector">
            <span className="om-page-size-label">Rows per page:</span>
            <select
              value={pageSize}
              onChange={e => {
                setPageSize(Number(e.target.value));
                setCurrentPage(1);
              }}
              className="om-page-size-select"
            >
              {[10, 20, 50, 100].map(size => (
                <option key={size} value={size}>{size}</option>
              ))}
            </select>
          </div>
          
          <div className="om-page-info">
            {startIndex + 1}-{endIndex} of {sortedOrders.length} orders
          </div>

          <div className="om-page-controls">
            <button
              className="om-page-btn"
              onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
              disabled={safeCurrentPage === 1}
              title="Previous Page"
            >
              ‹
            </button>
            <div className="om-page-numbers">
              {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                let pageNum = safeCurrentPage;
                if (totalPages <= 5) pageNum = i + 1;
                else if (safeCurrentPage <= 3) pageNum = i + 1;
                else if (safeCurrentPage >= totalPages - 2) pageNum = totalPages - 4 + i;
                else pageNum = safeCurrentPage - 2 + i;
                
                return (
                  <button
                    key={pageNum}
                    className={`om-page-num ${pageNum === safeCurrentPage ? 'active' : ''}`}
                    onClick={() => setCurrentPage(pageNum)}
                  >
                    {pageNum}
                  </button>
                );
              })}
            </div>
            <button
              className="om-page-btn"
              onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
              disabled={safeCurrentPage === totalPages}
              title="Next Page"
            >
              ›
            </button>
          </div>
        </div>
      )}

      {/* ─── Manual Order Modal ─── */}
      {manualModalOpen && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(15, 23, 42, 0.65)', backdropFilter: 'blur(4px)', zIndex: 9000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px' }}>
          <div style={{ background: '#fff', borderRadius: '12px', width: '100%', maxWidth: '900px', maxHeight: '90vh', display: 'flex', flexDirection: 'column', boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)', overflow: 'hidden' }}>
            {/* Header */}
            <div style={{ background: 'linear-gradient(135deg, #1e293b, #0f172a)', padding: '10px 16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexShrink: 0 }}>
              <div>
                <h2 style={{ color: '#f8fafc', margin: 0, fontSize: '1.05rem', fontWeight: '600', letterSpacing: '-0.025em' }}>Create Manual Order</h2>
                <p style={{ color: '#94a3b8', margin: '2px 0 0', fontSize: '0.7rem' }}>Record phone or call-in orders directly into the system</p>
              </div>
              <button onClick={() => setManualModalOpen(false)} style={{ background: 'rgba(255,255,255,0.1)', border: '1px solid rgba(255,255,255,0.1)', color: '#fff', borderRadius: '6px', width: '28px', height: '28px', fontSize: '1rem', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', transition: 'all 0.2s' }}>✕</button>
            </div>
            
            {/* Scrollable Body */}
            <div style={{ flex: 1, overflowY: 'auto' }}>

            {manualSuccess && (
              <div style={{ background: '#dcfce7', border: '1px solid #86efac', color: '#15803d', padding: '12px 24px', fontWeight: '600', fontSize: '0.85rem' }}>
                ✅ {manualSuccess}
              </div>
            )}

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 0 }}>
              {/* ── RIGHT: Product Search & Cart ── */}
              <div style={{ padding: '24px', borderLeft: '1px solid #e2e8f0', display: 'flex', flexDirection: 'column', gap: '16px', order: 2 }}>
                <h3 style={{ margin: 0, fontSize: '0.85rem', fontWeight: '600', color: '#1e293b', borderBottom: '2px solid #3b82f6', paddingBottom: '8px', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Products</h3>

                {/* Search */}
                <div style={{ position: 'relative' }}>
                  <input
                    value={manualProductSearch}
                    onChange={e => handleManualProductSearch(e.target.value)}
                    placeholder="Search products by name..."
                    style={{ width: '100%', padding: '8px 12px', borderRadius: '8px', border: '1px solid #d1d5db', fontSize: '0.8rem', boxSizing: 'border-box' }}
                  />
                  {manualProductSearching && <span style={{ position: 'absolute', right: '10px', top: '9px', fontSize: '0.7rem', color: '#9ca3af' }}>Searching...</span>}
                  {manualProductResults.length > 0 && !manualPickingProduct && (
                    <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, background: '#fff', border: '1px solid #e5e7eb', borderRadius: '8px', zIndex: 10, maxHeight: '220px', overflowY: 'auto', boxShadow: '0 4px 16px rgba(0,0,0,0.1)' }}>
                      {manualProductResults.map((p: any) => (
                        <div key={p.id} onClick={() => { setManualPickingProduct(p); setManualPickAttrs({ color: '', size: '', weight: '', qty: 1 }); setManualProductResults([]); }} style={{ padding: '8px 12px', cursor: 'pointer', display: 'flex', gap: '10px', alignItems: 'center', borderBottom: '1px solid #f3f4f6' }} onMouseEnter={e => (e.currentTarget.style.background = '#fef3c7')} onMouseLeave={e => (e.currentTarget.style.background = '')}>
                          {p.image && <img src={p.image} alt="" style={{ width: '38px', height: '38px', objectFit: 'cover', borderRadius: '4px', flexShrink: 0 }} />}
                          <div style={{ minWidth: 0 }}>
                            <div style={{ fontSize: '0.8rem', fontWeight: '600', color: '#1f2937', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{p.name_en}</div>
                            <div style={{ fontSize: '0.72rem', color: '#6b7280' }}>৳{parseFloat(p.discount_price || p.price).toFixed(2)} · Stock: {p.stock}</div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Attribute picker */}
                {manualPickingProduct && (
                  <div style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '8px', padding: '14px', display: 'flex', flexDirection: 'column', gap: '10px' }}>
                    <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                      {manualPickingProduct.image && <img src={manualPickingProduct.image} alt="" style={{ width: '48px', height: '48px', objectFit: 'cover', borderRadius: '6px', border: '1px solid #cbd5e1' }} />}
                      <div>
                        <div style={{ fontSize: '0.85rem', fontWeight: '600', color: '#1e293b' }}>{manualPickingProduct.name_en}</div>
                        <div style={{ fontSize: '0.75rem', color: '#2563eb', fontWeight: '600', marginTop: '2px' }}>৳{parseFloat(manualPickingProduct.discount_price || manualPickingProduct.price).toFixed(2)}</div>
                      </div>
                    </div>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '6px' }}>
                      {manualPickingProduct.attributes?.colors?.enabled && (
                        <div>
                          <label style={{ fontSize: '0.7rem', color: '#6b7280', display: 'block', marginBottom: '2px' }}>Color</label>
                          <select value={manualPickAttrs.color} onChange={e => setManualPickAttrs(p => ({ ...p, color: e.target.value }))} style={{ width: '100%', padding: '4px 6px', borderRadius: '6px', border: '1px solid #d1d5db', fontSize: '0.75rem' }}>
                            <option value="">— None —</option>
                            {(manualPickingProduct.attributes.colors.options || []).map((c: any) => (
                              <option key={c.name || c} value={c.name || c}>{c.name || c}</option>
                            ))}
                          </select>
                        </div>
                      )}
                      {manualPickingProduct.attributes?.sizes?.enabled && (
                        <div>
                          <label style={{ fontSize: '0.7rem', color: '#6b7280', display: 'block', marginBottom: '2px' }}>Size</label>
                          <select value={manualPickAttrs.size} onChange={e => setManualPickAttrs(p => ({ ...p, size: e.target.value }))} style={{ width: '100%', padding: '4px 6px', borderRadius: '6px', border: '1px solid #d1d5db', fontSize: '0.75rem' }}>
                            <option value="">— None —</option>
                            {(manualPickingProduct.attributes.sizes.options || []).map((s: any) => (
                              <option key={s.name || s} value={s.name || s}>{s.name || s}</option>
                            ))}
                          </select>
                        </div>
                      )}
                      {manualPickingProduct.attributes?.weights?.enabled && (
                        <div>
                          <label style={{ fontSize: '0.7rem', color: '#6b7280', display: 'block', marginBottom: '2px' }}>Weight</label>
                          <select value={manualPickAttrs.weight} onChange={e => setManualPickAttrs(p => ({ ...p, weight: e.target.value }))} style={{ width: '100%', padding: '4px 6px', borderRadius: '6px', border: '1px solid #d1d5db', fontSize: '0.75rem' }}>
                            <option value="">— None —</option>
                            {(manualPickingProduct.attributes.weights.options || []).map((w: any) => (
                              <option key={w.name || w} value={w.name || w}>{w.name || w}</option>
                            ))}
                          </select>
                        </div>
                      )}
                      <div>
                        <label style={{ fontSize: '0.7rem', color: '#6b7280', display: 'block', marginBottom: '2px' }}>Quantity</label>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                          <button onClick={() => setManualPickAttrs(p => ({ ...p, qty: Math.max(1, p.qty - 1) }))} style={{ width: '24px', height: '24px', borderRadius: '4px', border: '1px solid #d1d5db', background: '#f9fafb', cursor: 'pointer', fontSize: '0.8rem' }}>−</button>
                          <span style={{ fontSize: '0.85rem', fontWeight: '700', minWidth: '24px', textAlign: 'center' }}>{manualPickAttrs.qty}</span>
                          <button onClick={() => setManualPickAttrs(p => ({ ...p, qty: Math.min(Number(manualPickingProduct.stock || 9999), p.qty + 1) }))} style={{ width: '24px', height: '24px', borderRadius: '4px', border: '1px solid #d1d5db', background: '#f9fafb', cursor: 'pointer', fontSize: '0.8rem' }}>+</button>
                        </div>
                      </div>
                    </div>
                    <div style={{ display: 'flex', gap: '8px', marginTop: '4px' }}>
                      <button onClick={handleManualAddToCart} style={{ flex: 1, padding: '8px', borderRadius: '6px', background: '#2563eb', color: '#fff', border: 'none', fontSize: '0.8rem', fontWeight: '600', cursor: 'pointer', transition: 'background 0.2s' }} onMouseEnter={e => e.currentTarget.style.background = '#1d4ed8'} onMouseLeave={e => e.currentTarget.style.background = '#2563eb'}>Add to Cart</button>
                      <button onClick={() => setManualPickingProduct(null)} style={{ padding: '8px 14px', borderRadius: '6px', background: '#f1f5f9', color: '#475569', border: '1px solid #cbd5e1', fontSize: '0.8rem', fontWeight: '500', cursor: 'pointer', transition: 'background 0.2s' }} onMouseEnter={e => e.currentTarget.style.background = '#e2e8f0'} onMouseLeave={e => e.currentTarget.style.background = '#f1f5f9'}>Cancel</button>
                    </div>
                  </div>
                )}

                {/* Cart */}
                {manualCartItems.length > 0 && (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                    <div style={{ fontSize: '0.75rem', fontWeight: '600', color: '#6b7280' }}>CART ({manualCartItems.length} item{manualCartItems.length > 1 ? 's' : ''})</div>
                    {manualCartItems.map((it, idx) => (
                      <div key={idx} style={{ display: 'flex', gap: '8px', alignItems: 'center', background: '#f9fafb', borderRadius: '8px', padding: '8px', border: '1px solid #e5e7eb' }}>
                        {it.image && <img src={it.image} alt="" style={{ width: '36px', height: '36px', objectFit: 'cover', borderRadius: '4px', flexShrink: 0 }} />}
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ fontSize: '0.78rem', fontWeight: '600', color: '#1f2937', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{it.name}</div>
                          <div style={{ fontSize: '0.68rem', color: '#9ca3af' }}>
                            {[it.color, it.size, it.weight].filter(Boolean).join(' · ')}
                          </div>
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                          <button onClick={() => { const u = [...manualCartItems]; if (u[idx].qty > 1) u[idx] = { ...u[idx], qty: u[idx].qty - 1 }; else u.splice(idx, 1); setManualCartItems(u); }} style={{ width: '20px', height: '20px', borderRadius: '3px', border: '1px solid #d1d5db', background: '#fff', cursor: 'pointer', fontSize: '0.75rem', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>−</button>
                          <span style={{ fontSize: '0.8rem', fontWeight: '700', minWidth: '18px', textAlign: 'center' }}>{it.qty}</span>
                          <button onClick={() => { const u = [...manualCartItems]; u[idx] = { ...u[idx], qty: Math.min(Number(it.stock || 9999), it.qty + 1) }; setManualCartItems(u); }} style={{ width: '20px', height: '20px', borderRadius: '3px', border: '1px solid #d1d5db', background: '#fff', cursor: 'pointer', fontSize: '0.75rem', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>+</button>
                        </div>
                        <span style={{ fontSize: '0.8rem', fontWeight: '700', color: '#1f2937', minWidth: '60px', textAlign: 'right' }}>৳{(it.price * it.qty).toFixed(2)}</span>
                        <button onClick={() => setManualCartItems(prev => prev.filter((_, i) => i !== idx))} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#ef4444', fontSize: '0.9rem', padding: '0 2px' }}>✕</button>
                      </div>
                    ))}
                    <div style={{ textAlign: 'right', fontSize: '0.85rem', fontWeight: '600', color: '#334155', paddingRight: '4px', marginTop: '8px' }}>
                      Subtotal: <span style={{ color: '#2563eb' }}>৳{manualItemsSubtotal.toFixed(2)}</span>
                    </div>
                  </div>
                )}
                {manualCartItems.length === 0 && !manualPickingProduct && (
                  <div style={{ textAlign: 'center', padding: '40px 0', color: '#94a3b8', fontSize: '0.85rem', border: '1px dashed #cbd5e1', borderRadius: '8px', background: '#f8fafc' }}>
                    Search and add products above
                  </div>
                )}
              </div>

              {/* ── LEFT: Customer Info + Summary ── */}
              <div style={{ padding: '24px', display: 'flex', flexDirection: 'column', gap: '16px', order: 1 }}>
                <h3 style={{ margin: 0, fontSize: '0.85rem', fontWeight: '600', color: '#1e293b', borderBottom: '2px solid #3b82f6', paddingBottom: '8px', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Customer Info</h3>

                {[
                  { label: 'Customer Name *', key: 'customer_name', placeholder: 'Full name' },
                  { label: 'Phone Number *', key: 'customer_phone', placeholder: '01XXXXXXXXX' },
                ].map(f => (
                  <div key={f.key}>
                    <label style={{ fontSize: '0.72rem', fontWeight: '600', color: '#6b7280', display: 'block', marginBottom: '3px' }}>{f.label}</label>
                    <input
                      value={(manualForm as any)[f.key]}
                      onChange={e => setManualForm(p => ({ ...p, [f.key]: e.target.value }))}
                      placeholder={f.placeholder}
                      style={{ width: '100%', padding: '7px 10px', borderRadius: '7px', border: '1px solid #d1d5db', fontSize: '0.8rem', boxSizing: 'border-box' }}
                    />
                  </div>
                ))}

                <div>
                  <label style={{ fontSize: '0.72rem', fontWeight: '600', color: '#6b7280', display: 'block', marginBottom: '3px' }}>Shipping Address *</label>
                  <textarea
                    value={manualForm.shipping_address}
                    onChange={e => setManualForm(p => ({ ...p, shipping_address: e.target.value }))}
                    placeholder="Full delivery address"
                    rows={2}
                    style={{ width: '100%', padding: '7px 10px', borderRadius: '7px', border: '1px solid #d1d5db', fontSize: '0.8rem', resize: 'vertical', boxSizing: 'border-box' }}
                  />
                </div>

                <div>
                  <label style={{ fontSize: '0.72rem', fontWeight: '600', color: '#6b7280', display: 'block', marginBottom: '3px' }}>Notes (optional)</label>
                  <input
                    value={manualForm.notes}
                    onChange={e => setManualForm(p => ({ ...p, notes: e.target.value }))}
                    placeholder="Any special instructions..."
                    style={{ width: '100%', padding: '7px 10px', borderRadius: '7px', border: '1px solid #d1d5db', fontSize: '0.8rem', boxSizing: 'border-box' }}
                  />
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
                  <div>
                    <label style={{ fontSize: '0.72rem', fontWeight: '600', color: '#6b7280', display: 'block', marginBottom: '3px' }}>Payment Method</label>
                    <select value={manualForm.payment_method} onChange={e => setManualForm(p => ({ ...p, payment_method: e.target.value }))} style={{ width: '100%', padding: '7px 8px', borderRadius: '7px', border: '1px solid #d1d5db', fontSize: '0.78rem' }}>
                      <option value="cash_on_delivery">Cash on Delivery</option>
                      <option value="online_payment">Online Payment</option>
                      <option value="bank_transfer">Bank Transfer</option>
                    </select>
                  </div>
                  <div>
                    <label style={{ fontSize: '0.72rem', fontWeight: '600', color: '#6b7280', display: 'block', marginBottom: '3px' }}>Initial Status</label>
                    <select value={manualForm.initial_status} onChange={e => setManualForm(p => ({ ...p, initial_status: e.target.value }))} style={{ width: '100%', padding: '7px 8px', borderRadius: '7px', border: '1px solid #d1d5db', fontSize: '0.78rem' }}>
                      <option value="pending">Pending</option>
                      <option value="processing">Processing (Confirmed)</option>
                    </select>
                  </div>
                  <div>
                    <label style={{ fontSize: '0.72rem', fontWeight: '600', color: '#6b7280', display: 'block', marginBottom: '3px' }}>Delivery Charge (৳)</label>
                    <select 
                      value={manualForm.shipping_cost} 
                      onChange={e => setManualForm(p => ({ ...p, shipping_cost: e.target.value }))} 
                      style={{ width: '100%', padding: '7px 8px', borderRadius: '7px', border: '1px solid #d1d5db', fontSize: '0.8rem', boxSizing: 'border-box', background: '#fff' }}
                    >
                      <option value="60">Inside Dhaka (৳60)</option>
                      <option value="120">Outside Dhaka (৳120)</option>
                      <option value="0">Free Delivery (৳0)</option>
                    </select>
                  </div>
                </div>

                {/* Coupon */}
                <div>
                  <label style={{ fontSize: '0.72rem', fontWeight: '600', color: '#6b7280', display: 'block', marginBottom: '3px' }}>Coupon Code (optional)</label>
                  <div style={{ display: 'flex', gap: '6px' }}>
                    <input
                      value={manualForm.coupon_code}
                      onChange={e => { setManualForm(p => ({ ...p, coupon_code: e.target.value })); setManualCouponApplied(null); setManualCouponError(''); }}
                      placeholder="Enter coupon code"
                      style={{ flex: 1, padding: '7px 10px', borderRadius: '7px', border: `1px solid ${manualCouponApplied ? '#86efac' : manualCouponError ? '#fca5a5' : '#d1d5db'}`, fontSize: '0.8rem' }}
                    />
                    <button onClick={handleManualApplyCoupon} disabled={manualCouponChecking || !manualForm.coupon_code.trim()} style={{ padding: '7px 12px', borderRadius: '7px', background: '#6b7280', color: '#fff', border: 'none', fontSize: '0.75rem', fontWeight: '600', cursor: 'pointer', whiteSpace: 'nowrap' }}>
                      {manualCouponChecking ? '...' : 'Apply'}
                    </button>
                  </div>
                  {manualCouponApplied && <div style={{ fontSize: '0.72rem', color: '#16a34a', marginTop: '3px' }}>✅ Coupon applied: −৳{manualCouponApplied.discount.toFixed(2)}</div>}
                  {manualCouponError && <div style={{ fontSize: '0.72rem', color: '#dc2626', marginTop: '3px' }}>⚠️ {manualCouponError}</div>}
                </div>

                {/* Send email toggle */}
                <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', fontSize: '0.78rem', color: '#374151' }}>
                  <input type="checkbox" checked={manualForm.send_email} onChange={e => setManualForm(p => ({ ...p, send_email: e.target.checked }))} style={{ width: '15px', height: '15px' }} />
                  Send confirmation email to customer
                </label>

                {/* Order Summary */}
                <div style={{ background: '#f8fafc', borderRadius: '8px', padding: '16px', border: '1px solid #e2e8f0', marginTop: '8px' }}>
                  <div style={{ fontSize: '0.75rem', fontWeight: '700', color: '#64748b', marginBottom: '12px', letterSpacing: '0.05em' }}>ORDER SUMMARY</div>
                  {[
                    { label: 'Items Subtotal', value: `৳${manualItemsSubtotal.toFixed(2)}` },
                    { label: `Shipping`, value: `৳${manualShipping.toFixed(2)}` },
                    ...(manualCouponApplied ? [{ label: 'Coupon Discount', value: `−৳${manualCouponDiscount.toFixed(2)}`, red: true }] : []),
                  ].map(row => (
                    <div key={row.label} style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.8rem', color: (row as any).red ? '#ef4444' : '#475569', marginBottom: '6px' }}>
                      <span>{row.label}</span><span style={{ fontWeight: '500' }}>{row.value}</span>
                    </div>
                  ))}
                  <div style={{ borderTop: '1px solid #cbd5e1', marginTop: '10px', paddingTop: '10px', display: 'flex', justifyContent: 'space-between', fontWeight: '700', fontSize: '1rem', color: '#1e293b' }}>
                    <span>Total (COD)</span>
                    <span style={{ color: '#2563eb' }}>৳{(manualTotal < 0 ? 0 : manualTotal).toFixed(2)}</span>
                  </div>
                </div>

                {manualError && (
                  <div style={{ background: '#fef2f2', border: '1px solid #fca5a5', color: '#dc2626', padding: '10px 12px', borderRadius: '8px', fontSize: '0.78rem' }}>
                    ⚠️ {manualError}
                  </div>
                )}

                <button
                  onClick={handleManualCreateOrder}
                  disabled={manualSaving || manualCartItems.length === 0}
                  style={{
                    padding: '12px',
                    borderRadius: '8px',
                    background: (manualSaving || manualCartItems.length === 0) ? '#e2e8f0' : 'linear-gradient(135deg, #2563eb, #1d4ed8)',
                    color: (manualSaving || manualCartItems.length === 0) ? '#94a3b8' : '#fff',
                    border: 'none',
                    fontSize: '0.9rem',
                    fontWeight: '600',
                    cursor: (manualSaving || manualCartItems.length === 0) ? 'not-allowed' : 'pointer',
                    width: '100%',
                    marginTop: '8px',
                    boxShadow: (manualSaving || manualCartItems.length === 0) ? 'none' : '0 4px 12px rgba(37, 99, 235, 0.2)',
                    transition: 'all 0.2s'
                  }}
                  onMouseEnter={e => { if (!(manualSaving || manualCartItems.length === 0)) e.currentTarget.style.transform = 'translateY(-1px)'; }}
                  onMouseLeave={e => { if (!(manualSaving || manualCartItems.length === 0)) e.currentTarget.style.transform = 'none'; }}
                >
                  {manualSaving ? 'Creating Order...' : 'Create Order'}
                </button>
              </div>
            </div>
          </div>
          </div>
        </div>
      )}

      {/* ─── Edit Order Modal ─── */}
      {editModalOpen && editingOrder && (() => {
        const items: any[] = editingOrder.items || [];
        const itemsTotal = items.reduce((sum: number, it: any) => sum + parseFloat(it.subtotal || 0), 0);
        const shippingCost = parseFloat(editForm.shipping_cost) || 0;
        const discount = parseFloat(editingOrder.discount_amount || 0);
        const codAmount = itemsTotal + shippingCost - discount;
        const orderDate = editingOrder.created_at
          ? new Date(editingOrder.created_at).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })
          : new Date().toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });

        return (
          <div
            style={{
              position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
              backgroundColor: 'rgba(0,0,0,0.65)',
              display: 'flex',
              alignItems: isMobile ? 'flex-start' : 'center',
              justifyContent: 'center',
              zIndex: 10000, backdropFilter: 'blur(4px)',
              padding: isMobile ? '0' : '16px'
            }}
            onClick={isMobile ? undefined : closeEditModal}
          >
            <div
              style={{
                backgroundColor: 'white',
                borderRadius: isMobile ? '0' : '14px',
                width: '100%',
                maxWidth: isMobile ? '100%' : '1020px',
                height: isMobile ? '100dvh' : 'auto',
                maxHeight: isMobile ? '100dvh' : '95vh',
                display: 'flex', flexDirection: 'column',
                boxShadow: '0 20px 60px rgba(0,0,0,0.35)',
                overflow: 'hidden'
              }}
              onClick={(e) => e.stopPropagation()}
            >
              {/* ── Modal Header ── */}
              <div style={{
                padding: '10px 20px',
                background: 'linear-gradient(135deg, #1e293b, #0f172a)',
                color: 'white',
                display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                flexShrink: 0
              }}>
                <div>
                  <h2 style={{ margin: 0, fontSize: '1.05rem', fontWeight: '600', color: '#f8fafc', letterSpacing: '-0.025em' }}>Edit Order</h2>
                  <p style={{ margin: '2px 0 0', fontSize: '0.7rem', color: '#94a3b8' }}>#{editingOrder.order_number}</p>
                </div>
                <button onClick={closeEditModal} style={{
                  background: 'rgba(255,255,255,0.1)', border: '1px solid rgba(255,255,255,0.1)',
                  borderRadius: '6px', color: 'white', fontSize: '1rem',
                  width: '28px', height: '28px', cursor: 'pointer',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  transition: 'all 0.2s'
                }}>✕</button>
              </div>

              {/* ── Mobile Tab Bar ── */}
              {isMobile && (
                <div style={{ display: 'flex', borderBottom: '2px solid #e5e7eb', backgroundColor: '#f9fafb', flexShrink: 0 }}>
                  {([
                    { key: 'edit',  label: '✏️ Edit'  },
                    { key: 'slip',  label: '🧾 Slip'  },
                    { key: 'fraud', label: '🛡️ Fraud' },
                  ] as { key: 'edit' | 'slip' | 'fraud'; label: string }[]).map(tab => (
                    <button
                      key={tab.key}
                      onClick={() => setEditModalTab(tab.key)}
                      style={{
                        flex: 1, padding: '11px 6px',
                        border: 'none',
                        borderBottom: editModalTab === tab.key ? '2.5px solid #1a56db' : '2.5px solid transparent',
                        background: 'transparent',
                        color: editModalTab === tab.key ? '#1a56db' : '#6b7280',
                        fontWeight: editModalTab === tab.key ? '700' : '500',
                        fontSize: '13px', cursor: 'pointer',
                        transition: 'all 0.15s',
                      }}
                    >
                      {tab.label}
                    </button>
                  ))}
                </div>
              )}

              {/* ── Body: Desktop = two-column | Mobile = tab-driven ── */}
              <div style={{ display: 'flex', flex: 1, overflow: 'hidden', minHeight: 0 }}>

              {/* ── LEFT: Edit Form ── */}
              {(!isMobile || editModalTab === 'edit') && (
                <div style={{
                  width: isMobile ? '100%' : '340px',
                  minWidth: isMobile ? undefined : '300px',
                  flexShrink: 0,
                  padding: '16px 18px', overflowY: 'auto',
                  borderRight: isMobile ? 'none' : '1px solid #e2e8f0',
                  backgroundColor: '#f8fafc'
                }}>
                  <p style={{ margin: '0 0 10px', fontSize: '0.72rem', fontWeight: '700', color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.06em', borderBottom: '2px solid #3b82f6', paddingBottom: '6px', display: 'inline-block' }}>Move to Category</p>

                  {/* ── Category Quick-Set Buttons ── */}
                  <div style={{ display: 'flex', gap: '6px', marginBottom: '16px', flexWrap: 'wrap' }}>
                    {([
                      { key: 'future',       label: 'Future',       status: 'preorder',    color: '#6366f1', bg: '#eef2ff', border: '#c7d2fe', activeBg: '#4f46e5' },
                      { key: 'not_response', label: 'No Response',  status: 'no_response', color: '#b45309', bg: '#fef9ee', border: '#fde68a', activeBg: '#d97706' },
                      { key: 'confirmed',    label: 'Confirmed',    status: 'processing',  color: '#0f766e', bg: '#f0fdfb', border: '#99f6e4', activeBg: '#0d9488' },
                      { key: 'cancelled',    label: 'Cancelled',    status: 'cancelled',   color: '#be123c', bg: '#fff1f2', border: '#fecdd3', activeBg: '#e11d48' },
                    ] as { key: string; label: string; status: string; color: string; bg: string; border: string; activeBg: string }[]).map(cat => {
                      const group = Object.entries(STATUS_GROUPS).find(([, statuses]) => statuses.includes(editForm.status));
                      const isActive = group ? group[0] === cat.key : false;
                      return (
                        <button
                          key={cat.key}
                          onClick={() => setEditForm(prev => ({ ...prev, status: cat.status }))}
                          style={{
                            flex: '1 1 calc(50% - 3px)',
                            padding: '8px 6px',
                            border: `1.5px solid ${isActive ? cat.activeBg : cat.border}`,
                            borderRadius: '8px',
                            background: isActive ? cat.activeBg : cat.bg,
                            cursor: 'pointer',
                            fontSize: '0.75rem',
                            fontWeight: '600',
                            color: isActive ? '#fff' : cat.color,
                            transition: 'all 0.15s ease',
                            whiteSpace: 'nowrap',
                            textAlign: 'center',
                            boxShadow: isActive ? `0 2px 10px ${cat.activeBg}44` : 'none',
                            letterSpacing: '0.01em',
                          }}
                        >
                          {cat.label}
                        </button>
                      );
                    })}
                  </div>

                  <p style={{ margin: '16px 0 10px', fontSize: '0.72rem', fontWeight: '700', color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.06em', borderBottom: '2px solid #3b82f6', paddingBottom: '6px', display: 'inline-block' }}>Order Details</p>

                  {editError && (
                    <div style={{
                      background: '#fee2e2', color: '#b91c1c', border: '1px solid #fca5a5',
                      borderRadius: '8px', padding: '9px 12px', marginBottom: '14px', fontSize: '12px'
                    }}>⚠️ {editError}</div>
                  )}

                  {/* Status */}
                  <div style={{ marginBottom: '12px' }}>
                    <label style={{ display: 'block', fontWeight: '600', color: '#475569', marginBottom: '4px', fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.03em' }}>Order Status</label>
                    <select name="status" value={editForm.status} onChange={handleEditFormChange} style={{
                      width: '100%', padding: '8px 10px', border: '1px solid #cbd5e1',
                      borderRadius: '7px', fontSize: '0.85rem', color: '#1e293b',
                      backgroundColor: 'white', cursor: 'pointer', outline: 'none'
                    }}>
                      {STATUS_OPTIONS.map(s => (
                        <option key={s} value={s}>{s.charAt(0).toUpperCase() + s.slice(1).replace(/_/g, ' ')}</option>
                      ))}
                    </select>
                  </div>

                  {/* Customer Name */}
                  <div style={{ marginBottom: '12px' }}>
                    <label style={{ display: 'block', fontWeight: '600', color: '#475569', marginBottom: '4px', fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.03em' }}>Customer Name</label>
                    <input type="text" name="customer_name" value={editForm.customer_name} onChange={handleEditFormChange}
                      placeholder="Customer full name"
                      style={{ width: '100%', padding: '8px 10px', border: '1px solid #cbd5e1', borderRadius: '7px', fontSize: '0.85rem', color: '#1e293b', outline: 'none', boxSizing: 'border-box' }}
                    />
                  </div>

                  {/* Phone */}
                  <div style={{ marginBottom: '12px' }}>
                    <label style={{ display: 'block', fontWeight: '600', color: '#475569', marginBottom: '4px', fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.03em' }}>Phone Number</label>
                    <input type="text" name="customer_phone" value={editForm.customer_phone} onChange={handleEditFormChange}
                      placeholder="Customer phone"
                      style={{ width: '100%', padding: '8px 10px', border: '1px solid #cbd5e1', borderRadius: '7px', fontSize: '0.85rem', color: '#1e293b', outline: 'none', boxSizing: 'border-box' }}
                    />
                  </div>

                  {/* Shipping Address */}
                  <div style={{ marginBottom: '12px' }}>
                    <label style={{ display: 'block', fontWeight: '600', color: '#475569', marginBottom: '4px', fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.03em' }}>Shipping Address</label>
                    <textarea name="shipping_address" value={editForm.shipping_address} onChange={handleEditFormChange}
                      placeholder="Full shipping address" rows={3}
                      style={{ width: '100%', padding: '8px 10px', border: '1px solid #cbd5e1', borderRadius: '7px', fontSize: '0.85rem', color: '#1e293b', outline: 'none', resize: 'vertical', boxSizing: 'border-box', fontFamily: 'inherit' }}
                    />
                  </div>

                  {/* ── Delivery Option & Shipping Fee ── */}
                  <div style={{ marginBottom: '12px', padding: '10px 12px', background: '#fff', borderRadius: '8px', border: '1px solid #e2e8f0' }}>
                    <p style={{ margin: '0 0 10px', fontSize: '0.72rem', fontWeight: '700', color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.06em', borderBottom: '2px solid #3b82f6', paddingBottom: '6px', display: 'inline-block' }}>
                      🚚 Delivery Option &amp; Shipping Fee
                    </p>
                    
                    {/* Delivery Option Dropdown */}
                    <div style={{ marginBottom: '10px' }}>
                      <label style={{ display: 'block', fontSize: '0.7rem', fontWeight: '600', color: '#64748b', marginBottom: '4px' }}>
                        Shipping Option
                      </label>
                      <select
                        value={editForm.delivery_option || (deliveryOptions[0]?.id || '')}
                        onChange={(e) => {
                          const optId = e.target.value ? parseInt(e.target.value) : null;
                          handleDeliveryOptionChange(optId);
                        }}
                        style={{
                          width: '100%', padding: '8px 10px', border: '1px solid #cbd5e1',
                          borderRadius: '7px', fontSize: '0.85rem', color: '#1e293b', fontWeight: '600',
                          backgroundColor: '#f8fafc', cursor: 'pointer', outline: 'none',
                          boxSizing: 'border-box'
                        }}
                      >
                        {deliveryOptions.map(opt => (
                          <option key={opt.id} value={opt.id}>
                            {opt.icon || '🚚'} {opt.name_en} — ৳{parseFloat(opt.cost).toFixed(0)}
                          </option>
                        ))}
                      </select>
                    </div>

                    {/* Custom Shipping Fee Input */}
                    <div>
                      <label style={{ display: 'block', fontSize: '0.7rem', fontWeight: '600', color: '#64748b', marginBottom: '3px' }}>
                        Shipping Cost (৳)
                      </label>
                      <input
                        type="number"
                        min="0"
                        step="1"
                        name="shipping_cost"
                        value={editForm.shipping_cost}
                        onChange={(e) => setEditForm(prev => ({ ...prev, shipping_cost: e.target.value }))}
                        placeholder="Shipping fee in ৳"
                        style={{
                          width: '100%', padding: '7px 10px', border: '1px solid #cbd5e1',
                          borderRadius: '7px', fontSize: '0.85rem', fontWeight: '600',
                          color: '#1e293b', outline: 'none', boxSizing: 'border-box',
                          backgroundColor: '#fff'
                        }}
                      />
                    </div>
                  </div>

                  {/* Notes */}
                  <div style={{ marginBottom: '6px' }}>
                    <label style={{ display: 'block', fontWeight: '600', color: '#475569', marginBottom: '4px', fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.03em' }}>Notes</label>
                    <textarea name="notes" value={editForm.notes} onChange={handleEditFormChange}
                      placeholder="Optional order notes" rows={2}
                      style={{ width: '100%', padding: '8px 10px', border: '1px solid #cbd5e1', borderRadius: '7px', fontSize: '0.85rem', color: '#1e293b', outline: 'none', resize: 'vertical', boxSizing: 'border-box', fontFamily: 'inherit' }}
                    />
                  </div>

                  {/* ── Admin Discount ── */}
                  <div style={{ marginTop: '14px', padding: '12px 14px', background: '#fff', borderRadius: '8px', border: '1px solid #e2e8f0' }}>
                    <p style={{ margin: '0 0 10px', fontSize: '0.72rem', fontWeight: '700', color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.06em', borderBottom: '2px solid #3b82f6', paddingBottom: '6px', display: 'inline-block' }}>
                      Admin Discount
                    </p>

                    {/* Current discount badge */}
                    {editingOrder.discount_amount && parseFloat(editingOrder.discount_amount) > 0 && (
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '10px', padding: '7px 10px', background: '#d1fae5', borderRadius: '8px', border: '1px solid #6ee7b7' }}>
                        <span style={{ fontSize: '12px', fontWeight: '700', color: '#065f46' }}>
                          {editingOrder.admin_discount_type === 'percentage'
                            ? `−${editingOrder.admin_discount_value}% = ৳${parseFloat(editingOrder.discount_amount).toFixed(2)}`
                            : `−৳${parseFloat(editingOrder.discount_amount).toFixed(2)} (flat)`
                          }
                        </span>
                        <button
                          onClick={() => handleApplyDiscount(true)}
                          disabled={discountSaving}
                          title="Remove discount"
                          style={{ border: 'none', background: '#fca5a5', color: '#991b1b', borderRadius: '6px', padding: '3px 8px', fontSize: '11px', fontWeight: '700', cursor: 'pointer' }}
                        >✕ Remove</button>
                      </div>
                    )}

                    <div style={{ display: 'flex', gap: '6px', alignItems: 'flex-end' }}>
                      {/* Type selector */}
                      <div style={{ flex: '0 0 auto' }}>
                        <label style={{ display: 'block', fontSize: '11px', fontWeight: '600', color: '#6b7280', marginBottom: '4px' }}>Type</label>
                        <select
                          value={discountType}
                          onChange={(e) => { setDiscountType(e.target.value as 'flat' | 'percentage' | ''); setDiscountError(''); }}
                          style={{ padding: '7px 8px', border: '1px solid #d1d5db', borderRadius: '7px', fontSize: '12px', color: '#111827', background: 'white', cursor: 'pointer', outline: 'none' }}
                        >
                          <option value="">— Type —</option>
                          <option value="flat">৳ Flat</option>
                          <option value="percentage">% Percent</option>
                        </select>
                      </div>

                      {/* Value input */}
                      <div style={{ flex: 1 }}>
                        <label style={{ display: 'block', fontSize: '11px', fontWeight: '600', color: '#6b7280', marginBottom: '4px' }}>
                          Value {discountType === 'percentage' ? '(%)' : discountType === 'flat' ? '(৳)' : ''}
                        </label>
                        <input
                          type="number"
                          min="0.01"
                          step="0.01"
                          value={discountValue}
                          onChange={(e) => { setDiscountValue(e.target.value); setDiscountError(''); }}
                          placeholder={discountType === 'percentage' ? 'e.g. 10' : discountType === 'flat' ? 'e.g. 150' : ''}
                          style={{ width: '100%', padding: '7px 10px', border: '1px solid #d1d5db', borderRadius: '7px', fontSize: '13px', color: '#111827', outline: 'none', boxSizing: 'border-box' }}
                        />
                      </div>

                      {/* Apply button */}
                      <button
                        onClick={() => handleApplyDiscount(false)}
                        disabled={discountSaving || !discountType || !discountValue}
                        style={{
                          padding: '7px 12px',
                          border: 'none',
                          borderRadius: '7px',
                          fontSize: '12px',
                          fontWeight: '700',
                          cursor: discountSaving || !discountType || !discountValue ? 'not-allowed' : 'pointer',
                          background: discountSaving || !discountType || !discountValue ? '#e5e7eb' : 'linear-gradient(135deg, #16a34a, #15803d)',
                          color: discountSaving || !discountType || !discountValue ? '#9ca3af' : '#fff',
                          whiteSpace: 'nowrap',
                          transition: 'all 0.15s',
                          alignSelf: 'flex-end',
                          boxSizing: 'border-box',
                          height: '34px',
                        }}
                      >
                        {discountSaving ? '⏳' : '✓ Apply'}
                      </button>
                    </div>

                    {discountError && (
                      <div style={{ marginTop: '8px', padding: '6px 10px', background: '#fee2e2', color: '#b91c1c', borderRadius: '6px', fontSize: '11px' }}>
                        ⚠️ {discountError}
                      </div>
                    )}

                    {/* Live preview */}
                    {(() => {
                      const items = editingOrder.items || [];
                      const itemsSubtotal = items.reduce((s: number, i: any) => s + parseFloat(i.subtotal || 0), 0);
                      const shipping = parseFloat(editForm.shipping_cost) || 0;
                      const currentDiscount = parseFloat(editingOrder.discount_amount || 0);
                      const finalAmt = itemsSubtotal + shipping - currentDiscount;
                      return (
                        <div style={{ marginTop: '10px', padding: '8px 10px', background: '#f0fdf4', borderRadius: '8px', border: '1px solid #bbf7d0', fontSize: '11px', color: '#374151' }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '2px' }}><span>Items Subtotal</span><span style={{ fontWeight: '600' }}>৳{itemsSubtotal.toFixed(2)}</span></div>
                          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '2px' }}><span>Shipping</span><span style={{ fontWeight: '600' }}>৳{shipping.toFixed(2)}</span></div>
                          {currentDiscount > 0 && <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '2px', color: '#16a34a' }}><span>Discount</span><span style={{ fontWeight: '700' }}>−৳{currentDiscount.toFixed(2)}</span></div>}
                          <div style={{ display: 'flex', justifyContent: 'space-between', borderTop: '1px solid #bbf7d0', paddingTop: '4px', marginTop: '2px' }}><span style={{ fontWeight: '800', color: '#065f46' }}>COD (Final)</span><span style={{ fontWeight: '800', color: '#065f46', fontSize: '12px' }}>৳{finalAmt.toFixed(2)}</span></div>
                        </div>
                      );
                    })()}
                  </div>
                </div>
              )} {/* end LEFT panel */}

              {/* ── RIGHT: Packing Slip & Fraud ── */}
              {(!isMobile || editModalTab === 'slip' || editModalTab === 'fraud') && (
                <div style={{ flex: 1, overflowY: 'auto', padding: '20px', backgroundColor: '#fff' }}>
                  {(!isMobile || editModalTab === 'slip') && (<>
                  {/* Print button */}
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '14px' }}>
                    <p style={{ margin: '0 0 8px', fontSize: '0.72rem', fontWeight: '700', color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.06em', borderBottom: '2px solid #3b82f6', paddingBottom: '6px', display: 'inline-block' }}>Steadfast Packing Slip</p>
                    <button onClick={handlePrintPackingSlip} style={{
                      display: 'flex', alignItems: 'center', gap: '6px',
                      padding: '7px 14px', backgroundColor: '#0f172a', color: 'white',
                      border: 'none', borderRadius: '7px', fontSize: '12px',
                      fontWeight: '600', cursor: 'pointer'
                    }}>🖨️ Print Slip</button>
                  </div>

                  {/* ── Slip Content (ref for print) ── */}
                  <div ref={packingSlipRef} style={{
                    border: '1px solid #d1d5db', borderRadius: '8px',
                    overflow: 'hidden', fontSize: '13px', color: '#111', fontFamily: 'Arial, sans-serif'
                  }}>

                    {/* Slip Header */}
                    <div style={{
                      padding: '14px 18px',
                      background: '#111827',
                      display: 'flex', justifyContent: 'space-between', alignItems: 'center'
                    }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                        {siteSettings.logo_url && (
                          <img src={siteSettings.logo_url} alt="logo" style={{ height: '36px', objectFit: 'contain', borderRadius: '4px' }} />
                        )}
                        <div>
                          <div style={{ fontWeight: '800', fontSize: '15px', color: '#fff' }}>{siteSettings.site_name || 'Store'}</div>
                          {siteSettings.support_phone && (
                            <div style={{ fontSize: '11px', color: '#9ca3af' }}>📞 {siteSettings.support_phone}</div>
                          )}
                        </div>
                      </div>
                      <div style={{ textAlign: 'right' }}>
                        <div style={{ fontSize: '10px', color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.08em' }}>Packing Slip</div>
                        <div style={{ fontWeight: '700', fontSize: '14px', color: '#fff', marginTop: '2px' }}>STEADFAST COURIER</div>
                      </div>
                    </div>

                    {/* Invoice + Recipient row */}
                    <div style={{ display: 'flex', borderBottom: '1px solid #e5e7eb' }}>
                      {/* Ship To */}
                      <div style={{ flex: 1, padding: '13px 16px', borderRight: '1px solid #e5e7eb' }}>
                        <div style={{ fontSize: '10px', fontWeight: '700', color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '8px' }}>Ship To</div>
                        <div style={{ fontWeight: '700', fontSize: '14px', marginBottom: '3px' }}>{editForm.customer_name || '—'}</div>
                        <div style={{ color: '#374151', marginBottom: '3px' }}>📞 {editForm.customer_phone || '—'}</div>
                        <div style={{ color: '#374151', lineHeight: '1.5' }}>📍 {editForm.shipping_address || '—'}</div>
                        {editForm.notes && (
                          <div style={{ marginTop: '6px', fontSize: '11px', color: '#6b7280', fontStyle: 'italic' }}>Note: {editForm.notes}</div>
                        )}
                      </div>
                      {/* Order Info */}
                      <div style={{ width: '210px', flexShrink: 0, padding: '13px 16px', backgroundColor: '#f9fafb' }}>
                        <div style={{ fontSize: '10px', fontWeight: '700', color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '8px' }}>Order Info</div>
                        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px' }}>
                          <tbody>
                            {([
                              ['Invoice', editingOrder.order_number],
                              ['Date', orderDate],
                              ['Status', editForm.status.charAt(0).toUpperCase() + editForm.status.slice(1)],
                              ...(editingOrder.steadfast_tracking_code ? [['Tracking', editingOrder.steadfast_tracking_code]] : []),
                              ...(editingOrder.payment_method ? [['Payment', editingOrder.payment_method === 'cash_on_delivery' ? 'COD' : editingOrder.payment_method]] : []),
                            ] as [string, string][]).map(([k, v]) => (
                              <tr key={k}>
                                <td style={{ padding: '3px 0', color: '#6b7280', fontWeight: '600', paddingRight: '8px', whiteSpace: 'nowrap' }}>{k}</td>
                                <td style={{ padding: '3px 0', fontWeight: '700', wordBreak: 'break-all' }}>{v}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>

                    {/* Items Table */}
                    <div style={{ padding: '0' }}>
                      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
                        <thead>
                          <tr style={{ backgroundColor: '#f3f4f6' }}>
                            <th style={{ padding: '9px 14px', textAlign: 'left', fontWeight: '700', color: '#374151', borderBottom: '1px solid #e5e7eb' }}>#</th>
                            <th style={{ padding: '9px 14px', textAlign: 'left', fontWeight: '700', color: '#374151', borderBottom: '1px solid #e5e7eb' }}>Product</th>
                            <th style={{ padding: '9px 14px', textAlign: 'left', fontWeight: '700', color: '#374151', borderBottom: '1px solid #e5e7eb' }}>Variants</th>
                            <th style={{ padding: '9px 14px', textAlign: 'center', fontWeight: '700', color: '#374151', borderBottom: '1px solid #e5e7eb' }}>Qty</th>
                            <th style={{ padding: '9px 14px', textAlign: 'right', fontWeight: '700', color: '#374151', borderBottom: '1px solid #e5e7eb' }}>Unit Price</th>
                            <th style={{ padding: '9px 14px', textAlign: 'right', fontWeight: '700', color: '#374151', borderBottom: '1px solid #e5e7eb' }}>Subtotal</th>
                          </tr>
                        </thead>
                        <tbody>
                          {items.length === 0 ? (
                            <tr><td colSpan={6} style={{ padding: '14px', textAlign: 'center', color: '#9ca3af' }}>No items</td></tr>
                          ) : items.map((item: any, idx: number) => {
                            const color = item.selected_color || item.color || '';
                            const size = item.selected_size || item.size || '';
                            const weight = item.selected_weight || item.weight || '';
                            return (
                              <tr key={idx} style={{ borderBottom: '1px solid #f3f4f6', backgroundColor: idx % 2 === 0 ? 'white' : '#fafafa' }}>
                                <td style={{ padding: '9px 14px', color: '#6b7280' }}>{idx + 1}</td>
                                <td style={{ padding: '9px 14px', fontWeight: '600' }}>{item.product_name || item.name || 'Product'}</td>
                                <td style={{ padding: '9px 14px', color: '#64748b', fontSize: '12px' }}>
                                  {(color || size || weight) ? (
                                    <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap', alignItems: 'center' }}>
                                      {color && (
                                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', background: '#f5f3ff', color: '#7c3aed', border: '1px solid #ddd6fe', borderRadius: '12px', padding: '2px 8px', fontSize: '11px', fontWeight: 600 }}>
                                          <span style={{ width: '6px', height: '6px', borderRadius: '50%', background: color.toLowerCase() }} />
                                          {color}
                                        </span>
                                      )}
                                      {size && (
                                        <span style={{ display: 'inline-flex', alignItems: 'center', background: '#eef2ff', color: '#4f46e5', border: '1px solid #c7d2fe', borderRadius: '12px', padding: '2px 8px', fontSize: '11px', fontWeight: 600 }}>
                                          {size}
                                        </span>
                                      )}
                                      {weight && (
                                        <span style={{ display: 'inline-flex', alignItems: 'center', background: '#ecfdf5', color: '#059669', border: '1px solid #a7f3d0', borderRadius: '12px', padding: '2px 8px', fontSize: '11px', fontWeight: 600 }}>
                                          {weight}
                                        </span>
                                      )}
                                    </div>
                                  ) : '—'}
                                </td>
                                <td style={{ padding: '9px 14px', textAlign: 'center' }}>{item.quantity}</td>
                                <td style={{ padding: '9px 14px', textAlign: 'right' }}>৳{parseFloat(item.price || 0).toFixed(2)}</td>
                                <td style={{ padding: '9px 14px', textAlign: 'right', fontWeight: '600' }}>৳{parseFloat(item.subtotal || 0).toFixed(2)}</td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>

                    {/* Totals */}
                    <div style={{ borderTop: '1px solid #e5e7eb', display: 'flex', justifyContent: 'flex-end' }}>
                      <table style={{ width: '260px', borderCollapse: 'collapse', fontSize: '13px', margin: '0' }}>
                        <tbody>
                          <tr style={{ borderBottom: '1px solid #f3f4f6' }}>
                            <td style={{ padding: '8px 16px', color: '#6b7280' }}>Items Subtotal</td>
                            <td style={{ padding: '8px 16px', textAlign: 'right', fontWeight: '600' }}>৳{itemsTotal.toFixed(2)}</td>
                          </tr>
                          <tr style={{ borderBottom: '1px solid #f3f4f6' }}>
                            <td style={{ padding: '8px 16px', color: '#6b7280' }}>Shipping</td>
                            <td style={{ padding: '8px 16px', textAlign: 'right', fontWeight: '600' }}>৳{shippingCost.toFixed(2)}</td>
                          </tr>
                          {discount > 0 && (
                            <tr style={{ borderBottom: '1px solid #f3f4f6' }}>
                              <td style={{ padding: '8px 16px', color: '#16a34a' }}>Discount</td>
                              <td style={{ padding: '8px 16px', textAlign: 'right', fontWeight: '600', color: '#16a34a' }}>-৳{discount.toFixed(2)}</td>
                            </tr>
                          )}
                          <tr style={{ backgroundColor: '#111827' }}>
                            <td style={{ padding: '10px 16px', color: 'white', fontWeight: '800', fontSize: '14px' }}>COD Amount</td>
                            <td style={{ padding: '10px 16px', textAlign: 'right', color: 'white', fontWeight: '800', fontSize: '15px' }}>৳{codAmount.toFixed(2)}</td>
                          </tr>
                        </tbody>
                      </table>
                    </div>

                    {/* Footer note */}
                    <div style={{ padding: '10px 16px', backgroundColor: '#f9fafb', borderTop: '1px solid #e5e7eb', fontSize: '11px', color: '#9ca3af', textAlign: 'center' }}>
                      This is a system-generated packing slip for Steadfast Courier. Please attach to the parcel before dispatch.
                    </div>
                  </div>
                  </>)} {/* end slip tab */}

                  {/* ── Customer Fraud Check (Cross-Merchant) ── */}
                  {(!isMobile || editModalTab === 'fraud') && (
                  <div style={{ marginTop: isMobile ? '0' : '20px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '10px' }}>
                      <p style={{ fontSize: '11px', fontWeight: '700', color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.06em', margin: 0 }}>
                        🛡️ Customer Fraud Check
                      </p>
                      <button
                        onClick={() => editingOrder?.customer_phone && loadCustomerHistory(editingOrder.customer_phone)}
                        disabled={customerHistoryLoading}
                        title="Refresh fraud check"
                        style={{
                          border: 'none',
                          background: customerHistoryLoading ? '#e5e7eb' : 'linear-gradient(135deg, #3b82f6, #2563eb)',
                          color: 'white',
                          padding: '4px 10px',
                          borderRadius: '6px',
                          fontSize: '11px',
                          fontWeight: '600',
                          cursor: customerHistoryLoading ? 'not-allowed' : 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '4px',
                          transition: 'all 0.2s ease',
                          boxShadow: '0 1px 3px rgba(59,130,246,0.3)',
                        }}
                        onMouseEnter={(e) => !customerHistoryLoading && (e.currentTarget.style.transform = 'scale(1.05)')}
                        onMouseLeave={(e) => (e.currentTarget.style.transform = 'scale(1)')}
                      >
                        {customerHistoryLoading ? '⏳' : '🔄'} Refresh
                      </button>
                    </div>

                    {customerHistoryLoading && (
                      <div style={{ padding: '20px', textAlign: 'center', color: '#6b7280', fontSize: '13px' }}>
                        ⏳ Checking customer across courier networks...
                      </div>
                    )}

                    {customerHistoryError && (
                      <div style={{ padding: '12px', background: '#fee2e2', color: '#b91c1c', borderRadius: '8px', fontSize: '12px', marginBottom: '10px' }}>
                        ⚠️ {customerHistoryError}
                      </div>
                    )}

                    {!customerHistoryLoading && !customerHistoryError && !customerHistory && (
                      <div style={{ padding: '16px', textAlign: 'center', color: '#9ca3af', fontSize: '12px', background: '#f9fafb', borderRadius: '8px', border: '1px dashed #e5e7eb' }}>
                        No fraud check data available yet.
                      </div>
                    )}

                    {!customerHistoryLoading && customerHistory && (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                        {/* Courier Fraud Stats Cards */}
                        {['steadfast', 'pathao'].map((courier) => {
                          const data = customerHistory.fraud_check?.[courier];
                          if (!data) return null;
                          const hasError = !!data.error;
                          const isConfigError = hasError && data.error.includes('not configured');
                          const courierLabel = courier === 'steadfast' ? 'Steadfast' : 'Pathao';
                          const courierIcon = courier === 'steadfast' ? '🚚' : '📦';
                          const courierColor = courier === 'steadfast' ? '#6c3fbf' : '#e85d04';

                          if (isConfigError) {
                            return (
                              <div key={courier} style={{ border: '1px dashed #d1d5db', borderRadius: '8px', padding: '10px 14px', background: '#fafafa' }}>
                                <div style={{ fontSize: '11px', fontWeight: '700', color: '#9ca3af', marginBottom: '2px' }}>
                                  {courierIcon} {courierLabel}
                                </div>
                                <div style={{ fontSize: '11px', color: '#9ca3af' }}>Not configured — add login credentials in Integration Settings</div>
                              </div>
                            );
                          }

                          if (hasError) {
                            const isRateLimited = data.rate_limited;
                            return (
                              <div key={courier} style={{
                                border: `1px solid ${isRateLimited ? '#fde68a' : '#fecaca'}`,
                                borderRadius: '8px',
                                padding: '10px 14px',
                                background: isRateLimited ? '#fffbeb' : '#fef2f2',
                              }}>
                                <div style={{ fontSize: '11px', fontWeight: '700', color: courierColor, marginBottom: '4px' }}>
                                  {courierIcon} {courierLabel}
                                </div>
                                <div style={{ fontSize: '11px', color: isRateLimited ? '#92400e' : '#b91c1c', lineHeight: '1.4' }}>
                                  {isRateLimited ? '⏳' : '⚠️'} {data.error}
                                </div>
                                {isRateLimited && (
                                  <div style={{ fontSize: '10px', color: '#b45309', marginTop: '4px', fontStyle: 'italic' }}>
                                    💡 Results are cached — previously checked numbers won't use quota
                                  </div>
                                )}
                              </div>
                            );
                          }

                          const successRate = data.total > 0 ? Math.round((data.success / data.total) * 100) : 0;
                          const cancelRate = data.total > 0 ? Math.round((data.cancel / data.total) * 100) : 0;
                          const isFraudRisk = data.total >= 3 && cancelRate > 50;
                          const isGoodCustomer = data.total >= 3 && successRate >= 70;
                          const isNewCustomer = data.total === 0;

                          return (
                            <div key={courier} style={{
                              border: `1px solid ${isFraudRisk ? '#fecaca' : isGoodCustomer ? '#bbf7d0' : '#e5e7eb'}`,
                              borderRadius: '8px',
                              overflow: 'hidden',
                              background: isFraudRisk ? '#fff5f5' : isGoodCustomer ? '#f0fdf4' : '#fff',
                            }}>
                              {/* Courier Header */}
                              <div style={{
                                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                                padding: '8px 14px',
                                background: isFraudRisk ? '#fef2f2' : isGoodCustomer ? '#f0fdf4' : '#f8fafc',
                                borderBottom: `1px solid ${isFraudRisk ? '#fecaca' : isGoodCustomer ? '#bbf7d0' : '#e5e7eb'}`,
                              }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                  <span style={{ fontSize: '14px' }}>{courierIcon}</span>
                                  <span style={{ fontSize: '12px', fontWeight: '800', color: courierColor }}>{courierLabel}</span>
                                </div>
                                {isFraudRisk && (
                                  <span style={{ background: '#dc2626', color: '#fff', padding: '2px 8px', borderRadius: '10px', fontSize: '9px', fontWeight: '800', letterSpacing: '0.5px' }}>
                                    ⚠️ FRAUD RISK
                                  </span>
                                )}
                                {isGoodCustomer && (
                                  <span style={{ background: '#059669', color: '#fff', padding: '2px 8px', borderRadius: '10px', fontSize: '9px', fontWeight: '800', letterSpacing: '0.5px' }}>
                                    ✅ TRUSTED
                                  </span>
                                )}
                                {isNewCustomer && (
                                  <span style={{ background: '#6b7280', color: '#fff', padding: '2px 8px', borderRadius: '10px', fontSize: '9px', fontWeight: '800', letterSpacing: '0.5px' }}>
                                    🆕 NEW CUSTOMER
                                  </span>
                                )}
                                {!isFraudRisk && !isGoodCustomer && !isNewCustomer && (
                                  <span style={{ background: '#f59e0b', color: '#fff', padding: '2px 8px', borderRadius: '10px', fontSize: '9px', fontWeight: '800', letterSpacing: '0.5px' }}>
                                    ⚡ MODERATE
                                  </span>
                                )}
                              </div>

                              {/* Stats Row */}
                              <div style={{ display: 'flex', gap: '0' }}>
                                <div style={{ flex: 1, padding: '10px 8px', textAlign: 'center', borderRight: '1px solid #e5e7eb' }}>
                                  <div style={{ fontSize: '20px', fontWeight: '900', color: '#059669' }}>{data.success}</div>
                                  <div style={{ fontSize: '9px', color: '#059669', fontWeight: '700', textTransform: 'uppercase' }}>Delivered</div>
                                </div>
                                <div style={{ flex: 1, padding: '10px 8px', textAlign: 'center', borderRight: '1px solid #e5e7eb' }}>
                                  <div style={{ fontSize: '20px', fontWeight: '900', color: '#dc2626' }}>{data.cancel}</div>
                                  <div style={{ fontSize: '9px', color: '#dc2626', fontWeight: '700', textTransform: 'uppercase' }}>Cancelled</div>
                                </div>
                                <div style={{ flex: 1, padding: '10px 8px', textAlign: 'center', borderRight: '1px solid #e5e7eb' }}>
                                  <div style={{ fontSize: '20px', fontWeight: '900', color: '#111827' }}>{data.total}</div>
                                  <div style={{ fontSize: '9px', color: '#6b7280', fontWeight: '700', textTransform: 'uppercase' }}>Total</div>
                                </div>
                                <div style={{ flex: 1, padding: '10px 8px', textAlign: 'center' }}>
                                  <div style={{ fontSize: '20px', fontWeight: '900', color: successRate >= 70 ? '#059669' : successRate >= 40 ? '#f59e0b' : '#dc2626' }}>
                                    {data.total > 0 ? `${successRate}%` : '—'}
                                  </div>
                                  <div style={{ fontSize: '9px', color: '#6b7280', fontWeight: '700', textTransform: 'uppercase' }}>Success Rate</div>
                                </div>
                              </div>
                            </div>
                          );
                        })}

                        {/* Our Store Orders for this customer */}
                        {customerHistory.store_orders && customerHistory.store_orders.length > 0 && (
                          <div style={{ border: '1px solid #e5e7eb', borderRadius: '8px', overflow: 'hidden' }}>
                            <div style={{ padding: '8px 14px', background: '#f8fafc', borderBottom: '1px solid #e5e7eb', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                              <span style={{ fontSize: '11px', fontWeight: '700', color: '#374151' }}>
                                🏪 Our Store Orders ({customerHistory.store_order_count})
                              </span>
                            </div>
                            <div style={{ maxHeight: '180px', overflowY: 'auto' }}>
                              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '11px' }}>
                                <thead>
                                  <tr style={{ background: '#f3f4f6', position: 'sticky', top: 0, zIndex: 1 }}>
                                    <th style={{ padding: '6px 10px', textAlign: 'left', fontWeight: '700', color: '#374151', borderBottom: '1px solid #e5e7eb' }}>Order</th>
                                    <th style={{ padding: '6px 10px', textAlign: 'right', fontWeight: '700', color: '#374151', borderBottom: '1px solid #e5e7eb' }}>Amount</th>
                                    <th style={{ padding: '6px 10px', textAlign: 'left', fontWeight: '700', color: '#374151', borderBottom: '1px solid #e5e7eb' }}>Status</th>
                                    <th style={{ padding: '6px 10px', textAlign: 'left', fontWeight: '700', color: '#374151', borderBottom: '1px solid #e5e7eb' }}>Date</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {customerHistory.store_orders.map((rec: any, idx: number) => {
                                    const statusColors: Record<string, { bg: string; color: string; label: string }> = {
                                      delivered: { bg: '#d1fae5', color: '#065f46', label: 'Delivered' },
                                      shipped: { bg: '#dbeafe', color: '#1e40af', label: 'Shipped' },
                                      processing: { bg: '#fef3c7', color: '#92400e', label: 'Processing' },
                                      pending: { bg: '#f3f4f6', color: '#374151', label: 'Pending' },
                                      cancelled: { bg: '#fee2e2', color: '#991b1b', label: 'Cancelled' },
                                    };
                                    const st = statusColors[rec.status] || { bg: '#f3f4f6', color: '#374151', label: rec.status || 'Unknown' };
                                    const isCurrentOrder = rec.order_number === editingOrder?.order_number;
                                    return (
                                      <tr key={idx} style={{
                                        borderBottom: '1px solid #f3f4f6',
                                        background: isCurrentOrder ? '#eff6ff' : idx % 2 === 0 ? '#fff' : '#fafafa',
                                      }}>
                                        <td style={{ padding: '6px 10px', fontWeight: isCurrentOrder ? '700' : '500', color: isCurrentOrder ? '#1d4ed8' : '#111827', fontSize: '11px' }}>
                                          {rec.order_number || '—'}
                                          {isCurrentOrder && <span style={{ fontSize: '8px', color: '#2563eb', marginLeft: '3px' }}>(this)</span>}
                                        </td>
                                        <td style={{ padding: '6px 10px', textAlign: 'right', fontWeight: '600', fontSize: '11px' }}>৳{parseFloat(rec.total_amount || 0).toLocaleString()}</td>
                                        <td style={{ padding: '6px 10px' }}>
                                          <span style={{ background: st.bg, color: st.color, borderRadius: '10px', padding: '1px 7px', fontSize: '9px', fontWeight: '700', whiteSpace: 'nowrap' }}>{st.label}</span>
                                        </td>
                                        <td style={{ padding: '6px 10px', color: '#6b7280', fontSize: '10px', whiteSpace: 'nowrap' }}>
                                          {rec.created_at ? new Date(rec.created_at).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: '2-digit' }) : '—'}
                                        </td>
                                      </tr>
                                    );
                                  })}
                                </tbody>
                              </table>
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                  )} {/* end fraud tab */}
                </div>
              )} {/* end RIGHT panel */}
              </div>

              {/* ── Footer ── */}
              <div style={{
                padding: isMobile ? '12px 16px' : '12px 20px',
                borderTop: '1px solid #e2e8f0',
                display: 'flex',
                justifyContent: isMobile ? 'stretch' : 'flex-end',
                gap: '10px',
                backgroundColor: '#f8fafc', flexShrink: 0
              }}>
                <button onClick={closeEditModal} disabled={editSaving} style={{
                  flex: isMobile ? 1 : undefined,
                  padding: '9px 20px', backgroundColor: '#e2e8f0',
                  color: '#475569', border: 'none', borderRadius: '8px',
                  fontSize: '0.85rem', fontWeight: '600', cursor: 'pointer', transition: 'all 0.2s'
                }}
                  onMouseEnter={e => { e.currentTarget.style.backgroundColor = '#cbd5e1'; e.currentTarget.style.color = '#1e293b'; }}
                  onMouseLeave={e => { e.currentTarget.style.backgroundColor = '#e2e8f0'; e.currentTarget.style.color = '#475569'; }}
                >Cancel</button>
                <button onClick={handleSaveEdit} disabled={editSaving} style={{
                  flex: isMobile ? 2 : undefined,
                  padding: '9px 24px',
                  background: editSaving ? '#93c5fd' : 'linear-gradient(135deg, #2563eb, #1d4ed8)',
                  color: 'white', border: 'none', borderRadius: '8px',
                  fontSize: '0.85rem', fontWeight: '600',
                  cursor: editSaving ? 'not-allowed' : 'pointer',
                  display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px',
                  boxShadow: editSaving ? 'none' : '0 4px 12px rgba(37, 99, 235, 0.2)',
                  transition: 'all 0.2s'
                }}>
                  {editSaving ? <>Saving...</> : <>Save Changes</>}
                </button>
              </div>
            </div>
          </div>
        );
      })()}

      {/* Order View Modal */}
      {viewModalOpen && selectedOrder && (
        <div 
          className="modal-overlay" 
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.6)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 10000,
            backdropFilter: 'blur(4px)'
          }}
          onClick={closeViewModal}
        >
          <div 
            className="modal-content" 
            style={{
              backgroundColor: 'white',
              borderRadius: '12px',
              width: '90%',
              maxWidth: '800px',
              maxHeight: '90vh',
              display: 'flex',
              flexDirection: 'column',
              boxShadow: '0 10px 40px rgba(0,0,0,0.3)',
              animation: 'modalSlideIn 0.3s ease-out',
              overflow: 'hidden'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Modal Header - Fixed */}
            <div style={{
              padding: '12px 20px',
              borderBottom: '1px solid #e2e8f0',
              background: 'linear-gradient(135deg, #1e293b, #0f172a)',
              color: 'white',
              borderRadius: '12px 12px 0 0',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              flexShrink: 0
            }}>
              <div>
                <h2 style={{ margin: 0, fontSize: '1.1rem', fontWeight: '600', letterSpacing: '-0.025em', color: '#f8fafc' }}>Order Details</h2>
                <p style={{ margin: '2px 0 0', fontSize: '0.75rem', color: '#94a3b8' }}>Order #{selectedOrder.order_number}</p>
              </div>
              <button 
                onClick={closeViewModal}
                style={{
                  background: 'rgba(255,255,255,0.1)',
                  border: '1px solid rgba(255,255,255,0.1)',
                  borderRadius: '6px',
                  color: 'white',
                  fontSize: '1.1rem',
                  width: '28px',
                  height: '28px',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  transition: 'all 0.2s'
                }}
                onMouseEnter={(e) => e.currentTarget.style.background = 'rgba(255,255,255,0.2)'}
                onMouseLeave={(e) => e.currentTarget.style.background = 'rgba(255,255,255,0.1)'}
              >
                ✕
              </button>
            </div>

            {/* Modal Body - Scrollable */}
            <div 
              className="modal-body-scroll"
              style={{ 
                padding: '20px 24px',
                overflowY: 'auto',
                flex: 1,
                display: 'flex',
                flexDirection: 'column',
                gap: '20px'
              }}
            >
              {/* Top two-column summary bar */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: '12px' }}>
                {[
                  { label: 'Order Date', value: selectedOrder.created_at ? new Date(selectedOrder.created_at).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : 'N/A' },
                  { label: 'Source', value: selectedOrder.source === 'manual' ? 'Manual (Phone)' : 'Website' },
                  { label: 'Payment', value: selectedOrder.payment_method === 'cash_on_delivery' ? 'Cash on Delivery' : (selectedOrder.payment_method || 'COD') },
                  { label: 'Last Updated', value: selectedOrder.updated_at ? new Date(selectedOrder.updated_at).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : 'N/A' },
                ].map(card => (
                  <div key={card.label} style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '8px', padding: '12px 14px' }}>
                    <div style={{ fontSize: '0.7rem', fontWeight: '600', color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '4px' }}>{card.label}</div>
                    <div style={{ fontSize: '0.82rem', fontWeight: '600', color: '#1e293b' }}>{card.value}</div>
                  </div>
                ))}
              </div>

              {/* Customer Information */}
              <div>
                <h3 style={{ margin: '0 0 10px', fontSize: '0.78rem', fontWeight: '700', color: '#1e293b', borderBottom: '2px solid #3b82f6', paddingBottom: '6px', textTransform: 'uppercase', letterSpacing: '0.06em', display: 'inline-block' }}>
                  Customer Information
                </h3>
                <div style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '8px', overflow: 'hidden' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.84rem' }}>
                    <tbody>
                      {[
                        { label: 'Full Name', value: selectedOrder.customer_name },
                        { label: 'Phone', value: selectedOrder.customer_phone || 'N/A' },
                        { label: 'Shipping Address', value: selectedOrder.shipping_address },
                        { label: 'Delivery Option', value: (
                          <span>
                            <strong>{selectedOrder.delivery_option_name || (selectedOrder.is_inside_dhaka ? 'Inside Dhaka' : (parseFloat(selectedOrder.shipping_cost || 0) > 60 ? 'Outside Dhaka' : 'Standard Delivery'))}</strong>
                            <span style={{ color: '#2563eb', fontWeight: '600', marginLeft: '8px' }}>(৳{parseFloat(selectedOrder.shipping_cost || 0).toFixed(2)})</span>
                          </span>
                        )},
                        { label: 'Order Status', value: (
                          <span className={`status-badge ${selectedOrder.status?.toLowerCase() || 'pending'}`} style={{ display: 'inline-block', fontSize: '0.72rem', padding: '3px 10px' }}>
                            {(selectedOrder.status || 'Pending').replace(/_/g, ' ').replace(/\b\w/g, (c: string) => c.toUpperCase())}
                          </span>
                        )},
                      ].map((row, i, arr) => (
                        <tr key={row.label} style={{ borderBottom: i < arr.length - 1 ? '1px solid #e2e8f0' : 'none', backgroundColor: i % 2 === 0 ? '#f8fafc' : '#fff' }}>
                          <td style={{ padding: '9px 14px', fontWeight: '600', color: '#64748b', width: '28%', borderRight: '1px solid #e2e8f0', whiteSpace: 'nowrap' }}>{row.label}</td>
                          <td style={{ padding: '9px 14px', color: '#1e293b', lineHeight: '1.4' }}>{row.value}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Order Items */}
              <div>
                <h3 style={{ margin: '0 0 10px', fontSize: '0.78rem', fontWeight: '700', color: '#1e293b', borderBottom: '2px solid #3b82f6', paddingBottom: '6px', textTransform: 'uppercase', letterSpacing: '0.06em', display: 'inline-block' }}>
                  Order Items
                </h3>
                <div style={{ overflowX: 'auto', border: '1px solid #e2e8f0', borderRadius: '8px' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.84rem' }}>
                    <thead>
                      <tr style={{ backgroundColor: '#f1f5f9', color: '#475569', borderBottom: '2px solid #e2e8f0' }}>
                        <th style={{ padding: '9px 14px', textAlign: 'left', fontSize: '0.72rem', textTransform: 'uppercase', letterSpacing: '0.05em', fontWeight: '700' }}>#</th>
                        <th style={{ padding: '9px 14px', textAlign: 'left', fontSize: '0.72rem', textTransform: 'uppercase', letterSpacing: '0.05em', fontWeight: '700' }}>Product</th>
                        <th style={{ padding: '9px 14px', textAlign: 'left', fontSize: '0.72rem', textTransform: 'uppercase', letterSpacing: '0.05em', fontWeight: '700' }}>Variants</th>
                        <th style={{ padding: '9px 14px', textAlign: 'center', fontSize: '0.72rem', textTransform: 'uppercase', letterSpacing: '0.05em', fontWeight: '700' }}>Qty</th>
                        <th style={{ padding: '9px 14px', textAlign: 'right', fontSize: '0.72rem', textTransform: 'uppercase', letterSpacing: '0.05em', fontWeight: '700' }}>Unit Price</th>
                        <th style={{ padding: '9px 14px', textAlign: 'right', fontSize: '0.72rem', textTransform: 'uppercase', letterSpacing: '0.05em', fontWeight: '700' }}>Subtotal</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(selectedOrder.items || []).map((item: any, idx: number) => {
                        const color = item.selected_color || item.color || '';
                        const size = item.selected_size || item.size || '';
                        const weight = item.selected_weight || item.weight || '';
                        return (
                          <tr key={idx} style={{ borderBottom: '1px solid #e2e8f0', backgroundColor: idx % 2 === 0 ? 'white' : '#f8fafc' }}>
                            <td style={{ padding: '9px 14px', color: '#94a3b8', fontSize: '0.8rem' }}>{idx + 1}</td>
                            <td style={{ padding: '9px 14px', color: '#1e293b', fontWeight: '500' }}>{item.product_name || item.name || 'Unknown Product'}</td>
                            <td style={{ padding: '9px 14px', color: '#64748b', fontSize: '0.8rem' }}>
                              {(color || size || weight) ? (
                                <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap', alignItems: 'center' }}>
                                  {color && (
                                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', background: '#f5f3ff', color: '#7c3aed', border: '1px solid #ddd6fe', borderRadius: '12px', padding: '2px 8px', fontSize: '0.75rem', fontWeight: 600 }}>
                                      <span style={{ width: '6px', height: '6px', borderRadius: '50%', background: color.toLowerCase() }} />
                                      {color}
                                    </span>
                                  )}
                                  {size && (
                                    <span style={{ display: 'inline-flex', alignItems: 'center', background: '#eef2ff', color: '#4f46e5', border: '1px solid #c7d2fe', borderRadius: '12px', padding: '2px 8px', fontSize: '0.75rem', fontWeight: 600 }}>
                                      {size}
                                    </span>
                                  )}
                                  {weight && (
                                    <span style={{ display: 'inline-flex', alignItems: 'center', background: '#ecfdf5', color: '#059669', border: '1px solid #a7f3d0', borderRadius: '12px', padding: '2px 8px', fontSize: '0.75rem', fontWeight: 600 }}>
                                      {weight}
                                    </span>
                                  )}
                                </div>
                              ) : '—'}
                            </td>
                            <td style={{ padding: '9px 14px', textAlign: 'center', color: '#1e293b', fontWeight: '600' }}>{item.quantity}</td>
                            <td style={{ padding: '9px 14px', textAlign: 'right', color: '#64748b' }}>৳{parseFloat(item.price || 0).toFixed(2)}</td>
                            <td style={{ padding: '9px 14px', textAlign: 'right', color: '#2563eb', fontWeight: '600' }}>৳{parseFloat(item.subtotal || 0).toFixed(2)}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Payment Summary */}
              <div>
                <h3 style={{ margin: '0 0 10px', fontSize: '0.78rem', fontWeight: '700', color: '#1e293b', borderBottom: '2px solid #3b82f6', paddingBottom: '6px', textTransform: 'uppercase', letterSpacing: '0.06em', display: 'inline-block' }}>
                  Payment Summary
                </h3>
                <div style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '8px', padding: '16px', maxWidth: '380px', marginLeft: 'auto' }}>
                  {(() => {
                    const items = selectedOrder.items || [];
                    const itemsSubtotal = items.length > 0
                      ? items.reduce((s: number, i: any) => s + parseFloat(i.subtotal || (parseFloat(i.price || 0) * (i.quantity || 1)) || 0), 0)
                      : Math.max(0, (parseFloat(selectedOrder.total_amount || 0) - parseFloat(selectedOrder.shipping_cost || 0)));
                    const shipping = parseFloat(selectedOrder.shipping_cost) || 0;
                    const discount = parseFloat(selectedOrder.discount_amount) || 0;
                    const total = itemsSubtotal + shipping - discount;
                    return (
                      <>
                        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.84rem', color: '#475569', marginBottom: '7px' }}>
                          <span>Items Subtotal</span><span style={{ fontWeight: '500' }}>৳{itemsSubtotal.toFixed(2)}</span>
                        </div>
                        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.84rem', color: '#475569', marginBottom: '7px' }}>
                          <span>Shipping Charge</span><span style={{ fontWeight: '500' }}>৳{shipping.toFixed(2)}</span>
                        </div>
                        {discount > 0 && (
                          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.84rem', color: '#16a34a', marginBottom: '7px' }}>
                            <span>Discount {selectedOrder.coupon ? `(Coupon)` : selectedOrder.admin_discount_type ? `(Admin ${selectedOrder.admin_discount_type === 'percentage' ? selectedOrder.admin_discount_value + '%' : ''})` : ''}</span>
                            <span style={{ fontWeight: '600' }}>−৳{discount.toFixed(2)}</span>
                          </div>
                        )}
                        <div style={{ borderTop: '1px solid #cbd5e1', paddingTop: '10px', marginTop: '4px', display: 'flex', justifyContent: 'space-between', fontWeight: '700', fontSize: '1rem', color: '#1e293b' }}>
                          <span>Grand Total (COD)</span>
                          <span style={{ color: '#2563eb' }}>৳{total.toFixed(2)}</span>
                        </div>
                      </>
                    );
                  })()}
                </div>
              </div>

              {/* Steadfast Courier Info */}
              {selectedOrder.steadfast_consignment_id && (
                <div>
                  <h3 style={{ margin: '0 0 10px', fontSize: '0.78rem', fontWeight: '700', color: '#1e293b', borderBottom: '2px solid #3b82f6', paddingBottom: '6px', textTransform: 'uppercase', letterSpacing: '0.06em', display: 'inline-block' }}>
                    Courier / Shipping
                  </h3>
                  <div style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '8px', overflow: 'hidden' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.84rem' }}>
                      <tbody>
                        {[
                          { label: 'Consignment ID', value: selectedOrder.steadfast_consignment_id },
                          { label: 'Tracking Code', value: <span style={{ color: '#2563eb', fontWeight: '600' }}>{selectedOrder.steadfast_tracking_code}</span> },
                          { label: 'Courier Status', value: <span style={{ padding: '3px 10px', borderRadius: '5px', backgroundColor: '#dcfce7', color: '#15803d', fontSize: '0.75rem', fontWeight: '600' }}>{selectedOrder.steadfast_status || 'In Transit'}</span> },
                          ...(selectedOrder.steadfast_sent_at ? [{ label: 'Sent to Courier', value: new Date(selectedOrder.steadfast_sent_at).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) }] : []),
                        ].map((row, i, arr) => (
                          <tr key={row.label} style={{ borderBottom: i < arr.length - 1 ? '1px solid #e2e8f0' : 'none', backgroundColor: i % 2 === 0 ? '#f8fafc' : '#fff' }}>
                            <td style={{ padding: '9px 14px', fontWeight: '600', color: '#64748b', width: '28%', borderRight: '1px solid #e2e8f0' }}>{row.label}</td>
                            <td style={{ padding: '9px 14px', color: '#1e293b' }}>{row.value}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* Notes */}
              {selectedOrder.notes && (
                <div>
                  <h3 style={{ margin: '0 0 10px', fontSize: '0.78rem', fontWeight: '700', color: '#1e293b', borderBottom: '2px solid #3b82f6', paddingBottom: '6px', textTransform: 'uppercase', letterSpacing: '0.06em', display: 'inline-block' }}>
                    Order Notes
                  </h3>
                  <p style={{ padding: '12px 14px', backgroundColor: '#fffbeb', border: '1px solid #fde68a', borderRadius: '8px', color: '#92400e', fontSize: '0.84rem', margin: 0, lineHeight: '1.5' }}>
                    {selectedOrder.notes}
                  </p>
                </div>
              )}
            </div>


            {/* Modal Footer - Fixed */}
            <div style={{
              padding: '14px 20px',
              borderTop: '1px solid #e2e8f0',
              display: 'flex',
              justifyContent: 'flex-end',
              gap: '12px',
              backgroundColor: '#f8fafc',
              borderRadius: '0 0 12px 12px',
              flexShrink: 0
            }}>
              <button 
                onClick={closeViewModal}
                style={{
                  padding: '10px 24px',
                  backgroundColor: '#e2e8f0',
                  color: '#475569',
                  border: 'none',
                  borderRadius: '8px',
                  fontSize: '0.85rem',
                  fontWeight: '600',
                  cursor: 'pointer',
                  transition: 'all 0.2s'
                }}
                onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#cbd5e1'; e.currentTarget.style.color = '#1e293b'; }}
                onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = '#e2e8f0'; e.currentTarget.style.color = '#475569'; }}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ─── Bulk Send to Steadfast Result Modal ─── */}
      {bulkSendResult && (
        <div style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.65)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          zIndex: 10000, backdropFilter: 'blur(4px)', padding: '16px'
        }}>
          <div style={{
            backgroundColor: 'white', borderRadius: '14px',
            width: '100%', maxWidth: '680px', maxHeight: '90vh',
            display: 'flex', flexDirection: 'column',
            boxShadow: '0 20px 60px rgba(0,0,0,0.35)', overflow: 'hidden'
          }}>
            {/* Header */}
            <div style={{
              padding: '16px 24px',
              background: bulkSendResult.failed === 0
                ? 'linear-gradient(135deg, #059669, #065f46)'
                : bulkSendResult.sent === 0 && bulkSendResult.already_sent === 0
                ? 'linear-gradient(135deg, #dc2626, #991b1b)'
                : 'linear-gradient(135deg, #d97706, #92400e)',
              color: 'white',
              display: 'flex', justifyContent: 'space-between', alignItems: 'center',
              flexShrink: 0
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <span style={{ fontSize: '22px' }}>📦</span>
                <div>
                  <h2 style={{ margin: 0, fontSize: '17px', fontWeight: '700' }}>Send to Steadfast — Results</h2>
                  <p style={{ margin: 0, fontSize: '12px', opacity: 0.85 }}>
                    ✅ {bulkSendResult.sent} sent
                    {bulkSendResult.already_sent > 0 && <> &nbsp;·&nbsp; 🔵 {bulkSendResult.already_sent} already sent</>}
                    {bulkSendResult.failed > 0 && <> &nbsp;·&nbsp; ❌ {bulkSendResult.failed} failed</>}
                  </p>
                </div>
              </div>
              <button onClick={() => setBulkSendResult(null)} style={{
                background: 'rgba(255,255,255,0.15)', border: 'none',
                borderRadius: '8px', color: 'white', fontSize: '20px',
                width: '36px', height: '36px', cursor: 'pointer',
                display: 'flex', alignItems: 'center', justifyContent: 'center'
              }}>×</button>
            </div>
            {/* Body */}
            <div style={{ overflowY: 'auto', flex: 1, padding: '20px' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
                <thead>
                  <tr style={{ background: '#f3f4f6' }}>
                    <th style={{ padding: '9px 12px', textAlign: 'left', fontWeight: '700', color: '#374151', borderBottom: '1px solid #e5e7eb' }}>Order #</th>
                    <th style={{ padding: '9px 12px', textAlign: 'left', fontWeight: '700', color: '#374151', borderBottom: '1px solid #e5e7eb' }}>Status</th>
                    <th style={{ padding: '9px 12px', textAlign: 'left', fontWeight: '700', color: '#374151', borderBottom: '1px solid #e5e7eb' }}>Consignment ID</th>
                    <th style={{ padding: '9px 12px', textAlign: 'left', fontWeight: '700', color: '#374151', borderBottom: '1px solid #e5e7eb' }}>Tracking Code</th>
                    <th style={{ padding: '9px 12px', textAlign: 'left', fontWeight: '700', color: '#374151', borderBottom: '1px solid #e5e7eb' }}>Note</th>
                  </tr>
                </thead>
                <tbody>
                  {bulkSendResult.results.map((r: any, i: number) => (
                    <tr key={i} style={{ borderBottom: '1px solid #f3f4f6', background: r.already_sent ? '#eff6ff' : i % 2 === 0 ? '#fff' : '#fafafa' }}>
                      <td style={{ padding: '9px 12px', fontWeight: '600', color: '#111827' }}>{r.order}</td>
                      <td style={{ padding: '9px 12px' }}>
                        {r.already_sent
                          ? <span style={{ background: '#dbeafe', color: '#1e40af', borderRadius: '12px', padding: '2px 10px', fontSize: '11px', fontWeight: '700' }}>🔵 Already Sent</span>
                          : r.success
                          ? <span style={{ background: '#d1fae5', color: '#065f46', borderRadius: '12px', padding: '2px 10px', fontSize: '11px', fontWeight: '700' }}>✅ Sent</span>
                          : <span style={{ background: '#fee2e2', color: '#991b1b', borderRadius: '12px', padding: '2px 10px', fontSize: '11px', fontWeight: '700' }}>❌ Failed</span>
                        }
                      </td>
                      <td style={{ padding: '9px 12px', fontFamily: 'monospace', color: r.consignment_id ? '#1d4ed8' : '#9ca3af', fontWeight: r.consignment_id ? '700' : '400' }}>
                        {r.consignment_id || '—'}
                      </td>
                      <td style={{ padding: '9px 12px', fontFamily: 'monospace', color: r.tracking_code ? '#1d4ed8' : '#9ca3af' }}>
                        {r.tracking_code || '—'}
                      </td>
                      <td style={{ padding: '9px 12px', color: r.success ? '#6b7280' : '#dc2626', fontSize: '12px' }}>
                        {r.message || ''}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {/* Footer */}
            <div style={{ padding: '14px 24px', borderTop: '1px solid #e5e7eb', display: 'flex', justifyContent: 'flex-end' }}>
              <button onClick={() => setBulkSendResult(null)} style={{
                padding: '8px 22px', background: '#111827', color: 'white',
                border: 'none', borderRadius: '8px', fontSize: '13px',
                fontWeight: '600', cursor: 'pointer'
              }}>Close</button>
            </div>
          </div>
        </div>
      )}

      {/* ─── Pickup Request Result Modal (Manifest) ─── */}
      {pickupResult && (
        <div style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.65)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          zIndex: 10000, backdropFilter: 'blur(4px)', padding: '16px'
        }}>
          <div style={{
            backgroundColor: 'white', borderRadius: '14px',
            width: '100%', maxWidth: '720px', maxHeight: '90vh',
            display: 'flex', flexDirection: 'column',
            boxShadow: '0 20px 60px rgba(0,0,0,0.35)', overflow: 'hidden'
          }}>
            {/* Header */}
            <div style={{
              padding: '16px 24px',
              background: 'linear-gradient(135deg, #7c3aed, #5b21b6)',
              color: 'white',
              display: 'flex', justifyContent: 'space-between', alignItems: 'center',
              flexShrink: 0
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <span style={{ fontSize: '22px' }}>🚚</span>
                <div>
                  <h2 style={{ margin: 0, fontSize: '17px', fontWeight: '700' }}>Pickup Request — Manifest</h2>
                  <p style={{ margin: 0, fontSize: '12px', opacity: 0.85 }}>
                    ✅ {pickupResult.requested} requested
                    {(pickupResult as any).previously_requested > 0 && <> &nbsp;·&nbsp; 🔄 {(pickupResult as any).previously_requested} re-requested</>}
                    &nbsp;·&nbsp; Total COD: ৳{pickupResult.total_cod.toLocaleString()}
                  </p>
                </div>
              </div>
              <button onClick={() => setPickupResult(null)} style={{
                background: 'rgba(255,255,255,0.15)', border: 'none',
                borderRadius: '8px', color: 'white', fontSize: '20px',
                width: '36px', height: '36px', cursor: 'pointer',
                display: 'flex', alignItems: 'center', justifyContent: 'center'
              }}>×</button>
            </div>
            {/* Body */}
            <div style={{ overflowY: 'auto', flex: 1, padding: '20px' }}>
              {/* Info banner: pickup already pending */}
              {(pickupResult as any).pickup_already_pending && (
                <div style={{ background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: '10px', padding: '12px 16px', marginBottom: '16px', display: 'flex', alignItems: 'flex-start', gap: '10px' }}>
                  <span style={{ fontSize: '18px', flexShrink: 0 }}>ℹ️</span>
                  <div>
                    <div style={{ fontWeight: 700, color: '#1e40af', fontSize: '13px', marginBottom: '4px' }}>Pickup Already Scheduled</div>
                    <div style={{ color: '#1d4ed8', fontSize: '12px', lineHeight: '1.5' }}>A pending pickup request already exists for your address. These orders have been marked and will be included in the existing pickup.</div>
                  </div>
                </div>
              )}
              {/* API error banner */}
              {pickupResult.pickup_api_error && !(pickupResult as any).pickup_already_pending && (
                <div style={{ background: '#fef2f2', border: '1px solid #fecaca', borderRadius: '10px', padding: '12px 16px', marginBottom: '16px', display: 'flex', alignItems: 'flex-start', gap: '10px' }}>
                  <span style={{ fontSize: '18px', flexShrink: 0 }}>❌</span>
                  <div>
                    <div style={{ fontWeight: 700, color: '#991b1b', fontSize: '13px', marginBottom: '4px' }}>Pickup Request Failed</div>
                    <div style={{ color: '#b91c1c', fontSize: '12px', lineHeight: '1.5' }}>{pickupResult.pickup_api_error}</div>
                  </div>
                </div>
              )}
              {/* Summary cards */}
              <div style={{ display: 'flex', gap: '12px', marginBottom: '16px' }}>
                <div style={{ flex: 1, background: '#f3f4f6', borderRadius: '10px', padding: '12px 16px', textAlign: 'center' }}>
                  <div style={{ fontSize: '22px', fontWeight: '800', color: '#111827' }}>{pickupResult.requested + pickupResult.already_requested}</div>
                  <div style={{ fontSize: '11px', color: '#6b7280', fontWeight: '600' }}>Total Parcels</div>
                </div>
                <div style={{ flex: 1, background: '#ede9fe', borderRadius: '10px', padding: '12px 16px', textAlign: 'center' }}>
                  <div style={{ fontSize: '22px', fontWeight: '800', color: '#5b21b6' }}>৳{pickupResult.total_cod.toLocaleString()}</div>
                  <div style={{ fontSize: '11px', color: '#7c3aed', fontWeight: '600' }}>Total COD</div>
                </div>
                <div style={{ flex: 1, background: pickupResult.pickup_api_error ? '#fef2f2' : '#d1fae5', borderRadius: '10px', padding: '12px 16px', textAlign: 'center' }}>
                  <div style={{ fontSize: '22px', fontWeight: '800', color: pickupResult.pickup_api_error ? '#991b1b' : '#065f46' }}>{pickupResult.requested}</div>
                  <div style={{ fontSize: '11px', color: pickupResult.pickup_api_error ? '#b91c1c' : '#059669', fontWeight: '600' }}>Newly Requested</div>
                </div>
              </div>

              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
                <thead>
                  <tr style={{ background: '#f3f4f6' }}>
                    <th style={{ padding: '9px 12px', textAlign: 'left', fontWeight: '700', color: '#374151', borderBottom: '1px solid #e5e7eb' }}>#</th>
                    <th style={{ padding: '9px 12px', textAlign: 'left', fontWeight: '700', color: '#374151', borderBottom: '1px solid #e5e7eb' }}>Order</th>
                    <th style={{ padding: '9px 12px', textAlign: 'left', fontWeight: '700', color: '#374151', borderBottom: '1px solid #e5e7eb' }}>Customer</th>
                    <th style={{ padding: '9px 12px', textAlign: 'left', fontWeight: '700', color: '#374151', borderBottom: '1px solid #e5e7eb' }}>Consignment ID</th>
                    <th style={{ padding: '9px 12px', textAlign: 'right', fontWeight: '700', color: '#374151', borderBottom: '1px solid #e5e7eb' }}>COD</th>
                    <th style={{ padding: '9px 12px', textAlign: 'left', fontWeight: '700', color: '#374151', borderBottom: '1px solid #e5e7eb' }}>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {pickupResult.results.map((r: any, i: number) => (
                    <tr key={i} style={{ borderBottom: '1px solid #f3f4f6', background: r.already_requested ? '#f5f3ff' : i % 2 === 0 ? '#fff' : '#fafafa' }}>
                      <td style={{ padding: '9px 12px', color: '#6b7280', fontSize: '12px' }}>{i + 1}</td>
                      <td style={{ padding: '9px 12px', fontWeight: '600', color: '#111827' }}>{r.order}</td>
                      <td style={{ padding: '9px 12px', color: '#374151' }}>{r.customer_name || '—'}</td>
                      <td style={{ padding: '9px 12px', fontFamily: 'monospace', color: '#1d4ed8', fontWeight: '700' }}>
                        {r.consignment_id || '—'}
                      </td>
                      <td style={{ padding: '9px 12px', textAlign: 'right', fontWeight: '600', color: '#111827' }}>
                        ৳{r.cod_amount?.toLocaleString() || '—'}
                      </td>
                      <td style={{ padding: '9px 12px' }}>
                        {r.pickup_failed
                          ? <span style={{ background: '#fecaca', color: '#991b1b', borderRadius: '12px', padding: '2px 10px', fontSize: '11px', fontWeight: '700' }}>❌ Failed</span>
                          : r.pickup_already_pending
                            ? <span style={{ background: '#dbeafe', color: '#1e40af', borderRadius: '12px', padding: '2px 10px', fontSize: '11px', fontWeight: '700' }}>ℹ️ Pending</span>
                            : r.re_requested
                              ? <span style={{ background: '#dbeafe', color: '#1e40af', borderRadius: '12px', padding: '2px 10px', fontSize: '11px', fontWeight: '700' }}>🔄 Re-requested</span>
                              : <span style={{ background: '#d1fae5', color: '#065f46', borderRadius: '12px', padding: '2px 10px', fontSize: '11px', fontWeight: '700' }}>✅ Requested</span>
                        }
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {/* Footer */}
            <div style={{ padding: '14px 24px', borderTop: '1px solid #e5e7eb', display: 'flex', justifyContent: 'flex-end' }}>
              <button onClick={() => setPickupResult(null)} style={{
                padding: '8px 22px', background: '#111827', color: 'white',
                border: 'none', borderRadius: '8px', fontSize: '13px',
                fontWeight: '600', cursor: 'pointer'
              }}>Close</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// ── Steadfast status badge colors (used in edit modal tracking section) ──
// eslint-disable-next-line @typescript-eslint/no-unused-vars
function steadfastStatusStyle(status: string | null | undefined): { bg: string; text: string; icon: string } {
  switch (status?.toLowerCase()) {
    case 'in_review':
      return { bg: '#fef3c7', text: '#92400e', icon: '⏳' };
    case 'pending':
      return { bg: '#fef9c3', text: '#854d0e', icon: '🕐' };
    case 'in_transit':
      return { bg: '#dbeafe', text: '#1e40af', icon: '🚚' };
    case 'delivered':
      return { bg: '#d1fae5', text: '#065f46', icon: '✅' };
    case 'partial_delivered':
      return { bg: '#d1fae5', text: '#065f46', icon: '📦' };
    case 'cancelled':
      return { bg: '#fee2e2', text: '#991b1b', icon: '❌' };
    case 'hold':
      return { bg: '#fce7f3', text: '#9d174d', icon: '⏸️' };
    case 'delivered_approval_pending':
    case 'partial_delivered_approval_pending':
    case 'cancelled_approval_pending':
      return { bg: '#ede9fe', text: '#5b21b6', icon: '⏳' };
    case 'unknown':
      return { bg: '#f3f4f6', text: '#6b7280', icon: '❓' };
    default:
      return { bg: '#f3f4f6', text: '#6b7280', icon: '📋' };
  }
}

export default OrderManagement;
